import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, X, ChevronRight, ChevronDown, Clock, Trophy, Ticket, Loader2, Receipt, Trash2, DollarSign, Search, MoreHorizontal, Printer, Share2, ShieldCheck, Zap } from 'lucide-react';
import { TeamLogo, LeagueIcon } from '../components/sportsbook/TeamLogo';
import { leagueLogoUrl } from '../lib/teamLogos';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { announcePotContribution } from '../utils/potContribution';
import Header from '../components/Header';
import JackpotPromoBanner from '../components/JackpotPromoBanner';  // eslint-disable-line no-unused-vars -- community pots removed from UI (Jun 2026)
import DailySpecialBanner from '../components/DailySpecialBanner';
import FreePlayProgressBadge from '../components/FreePlayProgressBadge';
import SmartWagerHint from '../components/SmartWagerHint';
import LiveScoresTicker from '../components/LiveScoresTicker';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

const fireBetConfetti = () => {
    // Burst from bottom-center with the platform's brand colors
    const colors = ['#39FF14', '#FFD700', '#00FFFF', '#FF007F', '#FFFFFF'];
    const opts = { startVelocity: 50, spread: 70, ticks: 80, zIndex: 9999, colors, scalar: 0.9 };
    confetti({ ...opts, particleCount: 80, angle: 90, origin: { x: 0.5, y: 1.05 } });
    setTimeout(() => {
        confetti({ ...opts, particleCount: 50, angle: 60, origin: { x: 0, y: 1 } });
        confetti({ ...opts, particleCount: 50, angle: 120, origin: { x: 1, y: 1 } });
    }, 180);
};

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SPORT_ICONS = {
    'American Football': 'football-ball', 'Baseball': 'baseball-ball',
    'Basketball': 'basketball-ball', 'Ice Hockey': 'hockey-puck',
    'Soccer': 'futbol', 'Tennis': 'table-tennis', 'Boxing': 'fist-raised',
    'MMA': 'fist-raised', 'Golf': 'golf-ball',
};

function formatOdds(american) {
    return american > 0 ? `+${american}` : `${american}`;
}

// A pending bet is "awaiting result confirmation" once every event on it has
// almost certainly finished (~3.5h after the latest kickoff) but our team hasn't
// confirmed/settled it yet. Reinforces that wins are verified before payout.
const CONFIRM_DELAY_MS = 3.5 * 60 * 60 * 1000;
function awaitingConfirmation(bet) {
    if (!bet || bet.status !== 'pending') return false;
    const times = bet.type === 'single' ? [bet.commence_time] : (bet.legs || []).map((l) => l.commence_time);
    const valid = times.filter(Boolean).map((t) => new Date(t).getTime()).filter((n) => !isNaN(n));
    if (!valid.length) return false;
    return Date.now() > Math.max(...valid) + CONFIRM_DELAY_MS;
}


// Human label for a stored bet's market. For spread/total bets this appends the
// LOCKED point exactly as it was stored at placement time (never live data), so a
// customer's ticket always reflects the line they bought.
function formatLine(marketOrLeg) {
    const market = String(marketOrLeg?.market || '');
    const point = marketOrLeg?.point;
    if (market === 'h2h') return 'Moneyline';
    const pt = (point != null) ? `${point > 0 ? '+' : ''}${point}` : '';
    if (market === 'spreads') return `Spread ${pt}`.trim();
    if (market === 'totals') return `Total ${pt}`.trim();
    if (market.startsWith('player_') || market.startsWith('batter_') || market.startsWith('pitcher_')) return 'Prop';
    // Period / half / quarter or anything else: show market + locked point if any.
    return `${market}${pt ? ` ${pt}` : ''}`;
}

function formatTime(isoString) {
    const d = new Date(isoString);
    const now = new Date();
    const diff = d - now;
    if (diff < 0) return 'LIVE';

    // Force Central Time display
    const opts = { timeZone: 'America/Chicago' };
    const gameDateStr = d.toLocaleDateString('en-US', { ...opts, year: 'numeric', month: '2-digit', day: '2-digit' });
    const todayStr = now.toLocaleDateString('en-US', { ...opts, year: 'numeric', month: '2-digit', day: '2-digit' });

    const timeStr = d.toLocaleTimeString('en-US', { ...opts, hour: '2-digit', minute: '2-digit' });

    if (gameDateStr === todayStr) return timeStr;

    const tomorrow = new Date(now.getTime() + 86400000);
    const tomorrowStr = tomorrow.toLocaleDateString('en-US', { ...opts, year: 'numeric', month: '2-digit', day: '2-digit' });
    if (gameDateStr === tomorrowStr) return `Tomorrow ${timeStr}`;

    return d.toLocaleDateString('en-US', { ...opts, month: 'short', day: 'numeric' }) + ' ' + timeStr;
}

// Backend stores placed_at as a NAIVE UTC datetime, so it serializes without a
// 'Z' / offset. `new Date("...T02:46:16")` would then be parsed in the browser's
// LOCAL zone, throwing the Central conversion off by the local↔UTC offset
// (the "5 hours ahead" bug). Treat any tz-less timestamp as UTC.
function parseServerDate(dateStr) {
    if (!dateStr) return null;
    const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(dateStr);
    return new Date(hasTz ? dateStr : `${dateStr}Z`);
}

function formatDateCT(dateStr) {
    const d = parseServerDate(dateStr);
    if (!d || isNaN(d)) return '';
    return d.toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Team colors and abbreviations
const TEAM_DATA = {
    // NFL
    'Arizona Cardinals': { abbr: 'ARI', color: '#97233F' }, 'Atlanta Falcons': { abbr: 'ATL', color: '#A71930' },
    'Baltimore Ravens': { abbr: 'BAL', color: '#241773' }, 'Buffalo Bills': { abbr: 'BUF', color: '#00338D' },
    'Carolina Panthers': { abbr: 'CAR', color: '#0085CA' }, 'Chicago Bears': { abbr: 'CHI', color: '#0B162A' },
    'Cincinnati Bengals': { abbr: 'CIN', color: '#FB4F14' }, 'Cleveland Browns': { abbr: 'CLE', color: '#311D00' },
    'Dallas Cowboys': { abbr: 'DAL', color: '#003594' }, 'Denver Broncos': { abbr: 'DEN', color: '#FB4F14' },
    'Detroit Lions': { abbr: 'DET', color: '#0076B6' }, 'Green Bay Packers': { abbr: 'GB', color: '#203731' },
    'Houston Texans': { abbr: 'HOU', color: '#03202F' }, 'Indianapolis Colts': { abbr: 'IND', color: '#002C5F' },
    'Jacksonville Jaguars': { abbr: 'JAX', color: '#006778' }, 'Kansas City Chiefs': { abbr: 'KC', color: '#E31837' },
    'Las Vegas Raiders': { abbr: 'LV', color: '#A5ACAF' }, 'Los Angeles Chargers': { abbr: 'LAC', color: '#0080C6' },
    'Los Angeles Rams': { abbr: 'LAR', color: '#003594' }, 'Miami Dolphins': { abbr: 'MIA', color: '#008E97' },
    'Minnesota Vikings': { abbr: 'MIN', color: '#4F2683' }, 'New England Patriots': { abbr: 'NE', color: '#002244' },
    'New Orleans Saints': { abbr: 'NO', color: '#D3BC8D' }, 'New York Giants': { abbr: 'NYG', color: '#0B2265' },
    'New York Jets': { abbr: 'NYJ', color: '#125740' }, 'Philadelphia Eagles': { abbr: 'PHI', color: '#004C54' },
    'Pittsburgh Steelers': { abbr: 'PIT', color: '#FFB612' }, 'San Francisco 49ers': { abbr: 'SF', color: '#AA0000' },
    'Seattle Seahawks': { abbr: 'SEA', color: '#002244' }, 'Tampa Bay Buccaneers': { abbr: 'TB', color: '#D50A0A' },
    'Tennessee Titans': { abbr: 'TEN', color: '#4B92DB' }, 'Washington Commanders': { abbr: 'WAS', color: '#773141' },
    // NBA
    'Atlanta Hawks': { abbr: 'ATL', color: '#E03A3E' }, 'Boston Celtics': { abbr: 'BOS', color: '#007A33' },
    'Brooklyn Nets': { abbr: 'BKN', color: '#000000' }, 'Charlotte Hornets': { abbr: 'CHA', color: '#1D1160' },
    'Chicago Bulls': { abbr: 'CHI', color: '#CE1141' }, 'Cleveland Cavaliers': { abbr: 'CLE', color: '#860038' },
    'Dallas Mavericks': { abbr: 'DAL', color: '#00538C' }, 'Denver Nuggets': { abbr: 'DEN', color: '#0E2240' },
    'Detroit Pistons': { abbr: 'DET', color: '#C8102E' }, 'Golden State Warriors': { abbr: 'GSW', color: '#1D428A' },
    'Houston Rockets': { abbr: 'HOU', color: '#CE1141' }, 'Indiana Pacers': { abbr: 'IND', color: '#002D62' },
    'Los Angeles Clippers': { abbr: 'LAC', color: '#C8102E' }, 'Los Angeles Lakers': { abbr: 'LAL', color: '#552583' },
    'Memphis Grizzlies': { abbr: 'MEM', color: '#5D76A9' }, 'Miami Heat': { abbr: 'MIA', color: '#98002E' },
    'Milwaukee Bucks': { abbr: 'MIL', color: '#00471B' }, 'Minnesota Timberwolves': { abbr: 'MIN', color: '#0C2340' },
    'New Orleans Pelicans': { abbr: 'NOP', color: '#0C2340' }, 'New York Knicks': { abbr: 'NYK', color: '#006BB6' },
    'Oklahoma City Thunder': { abbr: 'OKC', color: '#007AC1' }, 'Orlando Magic': { abbr: 'ORL', color: '#0077C0' },
    'Philadelphia 76ers': { abbr: 'PHI', color: '#006BB6' }, 'Phoenix Suns': { abbr: 'PHX', color: '#E56020' },
    'Portland Trail Blazers': { abbr: 'POR', color: '#E03A3E' }, 'Sacramento Kings': { abbr: 'SAC', color: '#5A2D81' },
    'San Antonio Spurs': { abbr: 'SAS', color: '#C4CED4' }, 'Toronto Raptors': { abbr: 'TOR', color: '#CE1141' },
    'Utah Jazz': { abbr: 'UTA', color: '#002B5C' }, 'Washington Wizards': { abbr: 'WAS', color: '#002B5C' },
    // MLB
    'Arizona Diamondbacks': { abbr: 'ARI', color: '#A71930' }, 'Atlanta Braves': { abbr: 'ATL', color: '#CE1141' },
    'Baltimore Orioles': { abbr: 'BAL', color: '#DF4601' }, 'Boston Red Sox': { abbr: 'BOS', color: '#BD3039' },
    'Chicago Cubs': { abbr: 'CHC', color: '#0E3386' }, 'Chicago White Sox': { abbr: 'CWS', color: '#27251F' },
    'Cincinnati Reds': { abbr: 'CIN', color: '#C6011F' }, 'Cleveland Guardians': { abbr: 'CLE', color: '#00385D' },
    'Colorado Rockies': { abbr: 'COL', color: '#33006F' }, 'Detroit Tigers': { abbr: 'DET', color: '#0C2340' },
    'Houston Astros': { abbr: 'HOU', color: '#002D62' }, 'Kansas City Royals': { abbr: 'KC', color: '#004687' },
    'Los Angeles Angels': { abbr: 'LAA', color: '#BA0021' }, 'Los Angeles Dodgers': { abbr: 'LAD', color: '#005A9C' },
    'Miami Marlins': { abbr: 'MIA', color: '#00A3E0' }, 'Milwaukee Brewers': { abbr: 'MIL', color: '#12284B' },
    'Minnesota Twins': { abbr: 'MIN', color: '#002B5C' }, 'New York Mets': { abbr: 'NYM', color: '#002D72' },
    'New York Yankees': { abbr: 'NYY', color: '#003087' }, 'Oakland Athletics': { abbr: 'OAK', color: '#003831' },
    'Philadelphia Phillies': { abbr: 'PHI', color: '#E81828' }, 'Pittsburgh Pirates': { abbr: 'PIT', color: '#27251F' },
    'San Diego Padres': { abbr: 'SD', color: '#2F241D' }, 'San Francisco Giants': { abbr: 'SF', color: '#FD5A1E' },
    'Seattle Mariners': { abbr: 'SEA', color: '#0C2C56' }, 'St. Louis Cardinals': { abbr: 'STL', color: '#C41E3A' },
    'Tampa Bay Rays': { abbr: 'TB', color: '#092C5C' }, 'Texas Rangers': { abbr: 'TEX', color: '#003278' },
    'Toronto Blue Jays': { abbr: 'TOR', color: '#134A8E' }, 'Washington Nationals': { abbr: 'WSH', color: '#AB0003' },
    // NHL
    'Anaheim Ducks': { abbr: 'ANA', color: '#F47A38' }, 'Boston Bruins': { abbr: 'BOS', color: '#FFB81C' },
    'Buffalo Sabres': { abbr: 'BUF', color: '#002654' }, 'Calgary Flames': { abbr: 'CGY', color: '#D2001C' },
    'Carolina Hurricanes': { abbr: 'CAR', color: '#CC0000' }, 'Chicago Blackhawks': { abbr: 'CHI', color: '#CF0A2C' },
    'Colorado Avalanche': { abbr: 'COL', color: '#6F263D' }, 'Columbus Blue Jackets': { abbr: 'CBJ', color: '#002654' },
    'Dallas Stars': { abbr: 'DAL', color: '#006847' }, 'Detroit Red Wings': { abbr: 'DET', color: '#CE1126' },
    'Edmonton Oilers': { abbr: 'EDM', color: '#041E42' }, 'Florida Panthers': { abbr: 'FLA', color: '#041E42' },
    'Los Angeles Kings': { abbr: 'LAK', color: '#111111' }, 'Minnesota Wild': { abbr: 'MIN', color: '#154734' },
    'Montreal Canadiens': { abbr: 'MTL', color: '#AF1E2D' }, 'Nashville Predators': { abbr: 'NSH', color: '#FFB81C' },
    'New Jersey Devils': { abbr: 'NJD', color: '#CE1126' }, 'New York Islanders': { abbr: 'NYI', color: '#00539B' },
    'New York Rangers': { abbr: 'NYR', color: '#0038A8' }, 'Ottawa Senators': { abbr: 'OTT', color: '#C52032' },
    'Philadelphia Flyers': { abbr: 'PHI', color: '#F74902' }, 'Pittsburgh Penguins': { abbr: 'PIT', color: '#FCB514' },
    'San Jose Sharks': { abbr: 'SJS', color: '#006D75' }, 'Seattle Kraken': { abbr: 'SEA', color: '#001628' },
    'St. Louis Blues': { abbr: 'STL', color: '#002F87' }, 'Tampa Bay Lightning': { abbr: 'TB', color: '#002868' },
    'Toronto Maple Leafs': { abbr: 'TOR', color: '#00205B' }, 'Vancouver Canucks': { abbr: 'VAN', color: '#00205B' },
    'Vegas Golden Knights': { abbr: 'VGK', color: '#B4975A' }, 'Washington Capitals': { abbr: 'WSH', color: '#041E42' },
    'Winnipeg Jets': { abbr: 'WPG', color: '#041E42' },
};

function getTeamInfo(teamName) {
    if (TEAM_DATA[teamName]) return TEAM_DATA[teamName];
    // Fuzzy match — check if team name ends with a known team
    for (const [key, val] of Object.entries(TEAM_DATA)) {
        if (teamName.includes(key.split(' ').pop())) return val;
    }
    // Generate from name
    const words = teamName.split(' ');
    const abbr = words.length >= 2 ? (words[0][0] + words[words.length - 1][0]).toUpperCase() : teamName.substring(0, 3).toUpperCase();
    // Generate a consistent color from the name
    let hash = 0;
    for (let i = 0; i < teamName.length; i++) hash = teamName.charCodeAt(i) + ((hash << 5) - hash);
    const hue = Math.abs(hash) % 360;
    return { abbr, color: `hsl(${hue}, 60%, 45%)` };
}

function TeamBadge({ teamName, sportKey, size = 32 }) {
    return <TeamLogo name={teamName} sportKey={sportKey} size={size} />;
}

function calcPayout(amount, americanOdds) {
    const decimal = americanOdds > 0 ? (americanOdds / 100) + 1 : (100 / Math.abs(americanOdds)) + 1;
    return (amount * decimal).toFixed(2);
}

// Chip-row sport icon — official league logo when available, else a premium generic glyph.
const CHIP_GLYPH = { MLB: '⚾', NFL: '🏈', NCAAF: '🏈', NBA: '🏀', WNBA: '🏀', NCAAB: '🏀', NHL: '🏒', BOXING: '🥊', MMA: '🥋' };
const SportGlyph = ({ label, sportKey }) => {
    const [failed, setFailed] = useState(false);
    const url = failed ? null : leagueLogoUrl(sportKey);
    return (
        <span className="inline-flex items-center justify-center rounded-full overflow-hidden"
            style={{ width: 26, height: 26, background: 'radial-gradient(circle at 30% 25%, #232323, #0c0c0c)', border: '1px solid rgba(255,215,0,0.3)', fontSize: 15 }}>
            {url ? (
                <img src={url} alt={label || ''} onError={() => setFailed(true)}
                    style={{ width: '80%', height: '80%', objectFit: 'contain' }} />
            ) : (
                <span className="leading-none">{CHIP_GLYPH[label] || '🏆'}</span>
            )}
        </span>
    );
};

const Sportsbook = () => {
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const dailySpecialAppliedRef = useRef(null);
    const [sports, setSports] = useState({});
    const [selectedSport, setSelectedSport] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [betSlip, setBetSlip] = useState(() => {
        try { return JSON.parse(localStorage.getItem('betSlip') || '[]'); } catch { return []; }
    });
    const [betAmounts, setBetAmounts] = useState(() => {
        try { return JSON.parse(localStorage.getItem('betAmounts') || '{}'); } catch { return {}; }
    });
    const [showBetSlip, setShowBetSlip] = useState(false);
    const [myBets, setMyBets] = useState([]);
    const [showMyBets, setShowMyBets] = useState(false);
    const [cashoutQuotes, setCashoutQuotes] = useState({});
    const [myBetsTab, setMyBetsTab] = useState('active');
    const [cashoutHistory, setCashoutHistory] = useState({ rows: [], total_cashed_out: 0 });
    const [betScores, setBetScores] = useState({});
    const [receiptBet, setReceiptBet] = useState(null);

    // ---- Bet receipt (shareable / printable) -------------------------------
    const receiptTicketNo = (bet) => (bet?.bet_id || bet?._id || '').toString().slice(-8).toUpperCase() || 'TICKET';
    // Build a human label for a selection that ALWAYS includes the player (for
    // player props) and the market, e.g. "Jonathan Taylor Yes · Anytime TD".
    const pickLabel = (o) => {
        const player = (o?.description || '').trim();
        const core = (o?.outcome_name || '').trim();
        const mkt = (o?.market_label || '').trim();
        let label = core;
        if (player && !core.toLowerCase().startsWith(player.toLowerCase())) {
            label = `${player} ${core}`.trim();
        }
        if (mkt && !label.toLowerCase().includes(mkt.toLowerCase())) {
            label = label ? `${label} · ${mkt}` : mkt;
        }
        return label || core || mkt || '—';
    };
    const receiptRows = (bet) => {
        if (!bet) return [];
        if (bet.type === 'single') {
            return [{
                pick: pickLabel(bet),
                match: (bet.home_team && bet.away_team) ? `${bet.away_team} @ ${bet.home_team}` : (bet.event_name || ''),
                line: formatLine(bet),
                odds: formatOdds(bet.odds),
            }];
        }
        return (bet.legs || []).map((l) => ({
            pick: pickLabel(l),
            match: `${l.away_team} @ ${l.home_team}`,
            line: formatLine(l),
            odds: formatOdds(l.odds),
        }));
    };
    const receiptResult = (bet) =>
        bet.status === 'won' ? `WON  +${bet.potential_payout}` :
        bet.status === 'lost' ? `LOST  -${bet.amount}` :
        bet.status === 'cashed_out' ? `CASHED OUT  +${bet.cashout_amount}` :
        awaitingConfirmation(bet) ? 'PENDING CONFIRMATION' :
        `TO WIN  ${bet.potential_payout}`;
    const buildReceiptText = (bet) => {
        const rows = receiptRows(bet);
        return [
            'BETOHALICS — BET RECEIPT',
            `Ticket #${receiptTicketNo(bet)}`,
            `Placed: ${formatDateCT(bet.placed_at)} CT`,
            '',
            bet.type === 'single' ? 'SINGLE BET' : `PARLAY (${bet.legs?.length || 0} legs)`,
            ...rows.map((r) => `• ${r.pick} — ${r.line} @ ${r.odds}\n   ${r.match}`),
            '',
            `Wager: ${bet.amount}`,
            receiptResult(bet),
        ].join('\n');
    };
    const shareReceipt = async (bet) => {
        const text = buildReceiptText(bet);
        if (navigator.share) {
            try { await navigator.share({ title: 'BETOHALICS Bet Receipt', text }); return; } catch (e) { /* user cancelled / fall through */ }
        }
        try { await navigator.clipboard.writeText(text); toast.success('Receipt copied to clipboard'); }
        catch { toast.error('Could not copy receipt'); }
    };
    const printReceipt = (bet) => {
        const rows = receiptRows(bet);
        const esc = (s) => String(s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
        const rowsHtml = rows.map((r) => `
            <div class="leg">
                <div class="pick">${esc(r.pick)}</div>
                <div class="meta">${esc(r.line)} &nbsp;·&nbsp; <b>${esc(r.odds)}</b></div>
                <div class="match">${esc(r.match)}</div>
            </div>`).join('');
        const html = `<!doctype html><html><head><meta charset="utf-8"><title>Bet Receipt ${esc(receiptTicketNo(bet))}</title>
        <style>
            *{box-sizing:border-box} body{font-family:'Courier New',ui-monospace,monospace;background:#f4f4f4;margin:0;padding:24px;color:#111}
            .ticket{max-width:360px;margin:0 auto;background:#fff;border:1px dashed #999;border-radius:10px;padding:20px}
            .brand{text-align:center;font-weight:800;letter-spacing:3px;font-size:18px;border-bottom:2px solid #111;padding-bottom:8px;margin-bottom:8px}
            .sub{text-align:center;font-size:11px;color:#555;margin-bottom:12px}
            .kind{font-weight:800;font-size:13px;margin:10px 0 6px;text-transform:uppercase}
            .leg{padding:8px 0;border-bottom:1px dotted #ccc}
            .pick{font-weight:700;font-size:14px}
            .meta{font-size:12px;color:#333;margin-top:2px}
            .match{font-size:11px;color:#777;margin-top:2px}
            .totals{margin-top:12px;padding-top:10px;border-top:2px solid #111;font-size:13px;display:flex;justify-content:space-between}
            .result{font-weight:800}
            .foot{text-align:center;font-size:10px;color:#888;margin-top:14px}
            @media print{body{background:#fff;padding:0}.ticket{border:none}}
        </style></head><body>
            <div class="ticket">
                <div class="brand">BETOHALICS</div>
                <div class="sub">BET RECEIPT · Ticket #${esc(receiptTicketNo(bet))}<br/>Placed ${esc(formatDateCT(bet.placed_at))} CT</div>
                <div class="kind">${bet.type === 'single' ? 'Single Bet' : `Parlay · ${bet.legs?.length || 0} legs`}</div>
                ${rowsHtml}
                <div class="totals"><span>Wager</span><span>${esc(bet.amount)}</span></div>
                <div class="totals"><span>Result</span><span class="result">${esc(receiptResult(bet))}</span></div>
                <div class="foot">Lines & odds locked at time of purchase · betohalics.com</div>
            </div>
            <script>window.onload=function(){window.focus();window.print();}<\/script>
        </body></html>`;
        const w = window.open('', '_blank', 'width=420,height=640');
        if (!w) { toast.error('Pop-up blocked — allow pop-ups to print'); return; }
        w.document.open(); w.document.write(html); w.document.close();
    };
    const [placingBet, setPlacingBet] = useState(false);
    const [activeMarket, setActiveMarket] = useState('h2h');
    const [betMode, setBetMode] = useState('singles'); // 'singles' or 'parlay'
    const [parlayAmount, setParlayAmount] = useState('');
    const [showProps, setShowProps] = useState(false);
    const [propsEvents, setPropsEvents] = useState([]);
    const [selectedPropsEvent, setSelectedPropsEvent] = useState(null);
    const [propsData, setPropsData] = useState([]);
    const [propsLoading, setPropsLoading] = useState(false);
    const [liveFilter, setLiveFilter] = useState(false);
    const [sportSearch, setSportSearch] = useState('');
    const [showAllSports, setShowAllSports] = useState(false);
    const [liveSlipStatus, setLiveSlipStatus] = useState({});
    const isLiveMode = selectedSport?.key === '__live__';
    const [moreOpen, setMoreOpen] = useState(false);
    const [featured, setFeatured] = useState(null);
    const SB = '/sportsbook';

    // Persist bet slip to localStorage
    useEffect(() => {
        localStorage.setItem('betSlip', JSON.stringify(betSlip));
    }, [betSlip]);
    useEffect(() => {
        localStorage.setItem('betAmounts', JSON.stringify(betAmounts));
    }, [betAmounts]);

    // Sportsbook risk limits (public pre-check) — surfaced on the bet slip so a
    // customer sees a clean message BEFORE submitting instead of a rejection after.
    const [limits, setLimits] = useState(null);
    useEffect(() => {
        axios.get(`${API}/sports/limits`)
            .then(({ data }) => setLimits(data))
            .catch(() => setLimits(null));
    }, []);

    // Fetch sports list
    useEffect(() => {
        const fetchSports = async () => {
            try {
                const { data } = await axios.get(`${API}/sports`, {
                    headers: { 'Cache-Control': 'no-cache' },
                });
                setSports(data.groups || {});
                // Auto-select a popular sport with events
                const allSports = Object.values(data.groups || {}).flat();
                const preferred = ['basketball_nba', 'icehockey_nhl', 'baseball_mlb', 'americanfootball_nfl', 'soccer_epl'];
                const pick = allSports.find(s => preferred.includes(s.key)) ||
                    allSports.find(s => s.featured && !s.has_outrights) ||
                    allSports.find(s => !s.has_outrights);
                if (pick) setSelectedSport(pick);
            } catch (e) { console.error('Sports fetch error:', e); }
            setLoading(false);
        };
        fetchSports();
    }, []);

    // Fetch odds when sport changes
    const fetchOdds = useCallback(async (sport) => {
        if (!sport) return;
        setEventsLoading(true);
        setFetchError(null);
        try {
            const isLive = sport.key === '__live__';
            const url = isLive ? `${API}/sports/live/odds` : `${API}/sports/${sport.key}/odds`;
            const { data } = await axios.get(url, {
                params: { _t: Date.now() },
                headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
                timeout: 20000,
                withCredentials: true,
            });
            setEvents(data.events || []);
            if (!data.events || data.events.length === 0) {
                setFetchError(isLive ? 'no_live' : 'no_events');
            }
        } catch (e) {
            console.error('Odds fetch error:', e);
            const status = e.response?.status;
            const detail = e.response?.data?.detail;
            let msg;
            if (status === 429 || (detail && detail.includes('rate limit'))) {
                msg = 'Rate limit reached — retrying in a few seconds...';
                // Auto-retry after 5 seconds
                setTimeout(() => fetchOdds(sport), 5000);
            } else if (detail) {
                msg = detail;
            } else if (e.code === 'ECONNABORTED' || e.message?.includes('timeout')) {
                msg = 'Request timed out. Tap Retry.';
            } else {
                msg = e.message || 'Network error';
            }
            setFetchError(msg);
            setEvents([]);
        }
        setEventsLoading(false);
    }, []);

    useEffect(() => {
        fetchOdds(selectedSport);
    }, [selectedSport, fetchOdds]);

    // Auto-refresh: live mode polls every 10s; a normal sport with in-progress
    // games keeps the slower 30s refresh.
    useEffect(() => {
        if (!selectedSport) return;
        if (isLiveMode) {
            const interval = setInterval(() => fetchOdds(selectedSport), 10000);
            return () => clearInterval(interval);
        }
        if (activeMarket === 'props') return;
        const hasLive = events.some(e => new Date(e.commence_time) <= new Date());
        if (!hasLive) return;
        const interval = setInterval(() => fetchOdds(selectedSport), 30000);
        return () => clearInterval(interval);
    }, [selectedSport, events, activeMarket, fetchOdds, isLiveMode]);

    // Live line-move / suspension status for each bet-slip selection. Compares
    // the price each pick was added at against the current live feed; marks a
    // pick 'changed' (needs re-accept) or 'unavailable' (market dropped/stale).
    useEffect(() => {
        if (!isLiveMode) { setLiveSlipStatus({}); return; }
        const map = {};
        events.forEach(e => {
            Object.entries(e.markets || {}).forEach(([mk, m]) => {
                (m.outcomes || []).forEach(o => {
                    map[`${e.id}_${mk}_${o.name}`] = { price: o.price, stale: !!m.stale };
                });
            });
        });
        const status = {};
        betSlip.forEach(b => {
            const live = new Date(b.commence_time) <= new Date();
            if (!live) return;
            if (!['h2h', 'spreads', 'totals'].includes(b.market)) return; // only core live lines re-check
            const cur = map[b.key];
            if (!cur || cur.stale) status[b.key] = { suspended: 'unavailable' };
            else if (Number(cur.price) !== Number(b.odds)) status[b.key] = { suspended: 'changed', newOdds: cur.price };
        });
        setLiveSlipStatus(status);
    }, [events, betSlip, isLiveMode]);

    const acceptNewOdds = useCallback((key, newOdds) => {
        setBetSlip(prev => prev.map(b => b.key === key ? { ...b, odds: newOdds } : b));
        setLiveSlipStatus(prev => { const n = { ...prev }; delete n[key]; return n; });
    }, []);

    // Live extra markets (in-play player props + period/half/quarter), loaded per game on demand.
    const [liveExtra, setLiveExtra] = useState({});
    const toggleLiveExtra = useCallback(async (event) => {
        const id = event.id;
        setLiveExtra(prev => ({ ...prev, [id]: { ...(prev[id] || {}), open: !(prev[id]?.open) } }));
        const already = liveExtra[id];
        if (already && (already.periods || already.props)) return; // cached
        setLiveExtra(prev => ({ ...prev, [id]: { ...(prev[id] || {}), open: true, loading: true } }));
        try {
            const { data } = await axios.get(`${API}/sports/${event.sport_key}/events/${id}/live-extra`, { withCredentials: true });
            setLiveExtra(prev => ({ ...prev, [id]: { open: true, loading: false, periods: data.periods || [], props: data.props || [] } }));
        } catch {
            setLiveExtra(prev => ({ ...prev, [id]: { open: true, loading: false, periods: [], props: [] } }));
        }
    }, [liveExtra]);

    // Fetch props events when switching to props view
    const fetchPropsEvents = useCallback(async (sport) => {
        if (!sport) return;
        setPropsLoading(true);
        try {
            const { data } = await axios.get(`${API}/sports/${sport.key}/events`);
            setPropsEvents(data.events || []);
            setSelectedPropsEvent(null);
            setPropsData([]);
        } catch { setPropsEvents([]); }
        setPropsLoading(false);
    }, []);

    // Fetch props for a specific event
    const fetchEventProps = useCallback(async (sport, eventId) => {
        if (!sport || !eventId) return;
        setPropsLoading(true);
        try {
            const { data } = await axios.get(`${API}/sports/${sport.key}/events/${eventId}/props`);
            setSelectedPropsEvent(data.event || null);
            setPropsData(data.props || []);
        } catch { setPropsData([]); }
        setPropsLoading(false);
    }, []);

    // When switching to props market, load events
    useEffect(() => {
        if (activeMarket === 'props' && selectedSport) {
            fetchPropsEvents(selectedSport);
        }
    }, [activeMarket, selectedSport, fetchPropsEvents]);


    // Add to bet slip
    const addToBetSlip = useCallback((event, market, outcome) => {
        // Player props always carry the player's name so the ticket (and the server price) is for THAT player
        const desc = (outcome.description || '').trim();
        const name = desc && !outcome.name.startsWith(`${desc} `)
            ? `${desc} ${outcome.name}${outcome.point != null ? ' ' + outcome.point : ''}` : outcome.name;
        const key = `${event.id}_${market}_${name}`;
        // Remove if already in slip
        if (betSlip.find(b => b.key === key)) {
            setBetSlip(prev => prev.filter(b => b.key !== key));
            return;
        }
        setBetSlip(prev => [...prev, {
            key,
            event_id: event.id,
            sport_key: event.sport_key,
            market,
            outcome_name: name,
            description: outcome.description || null,
            market_label: outcome.market_label || null,
            odds: outcome.price,
            point: outcome.point || null,
            home_team: event.home_team,
            away_team: event.away_team,
            commence_time: event.commence_time,
            matchup: `${event.away_team} @ ${event.home_team}`,
        }]);
        setShowBetSlip(true);
    }, [betSlip]);

    const removeBet = (key) => {
        setBetSlip(prev => prev.filter(b => b.key !== key));
        setBetAmounts(prev => { const n = {...prev}; delete n[key]; return n; });
    };

    const clearBetSlip = () => {
        setBetSlip([]);
        setBetAmounts({});
        setParlayAmount('');
        localStorage.removeItem('betSlip');
        localStorage.removeItem('betAmounts');
    };

    // ── Daily Special deep-link.
    // When the Lobby/Sportsbook banner navigates here with ?special=...&event=...&market=...&outcome=...
    // we auto-select the right sport, find the matching event in the events list, and add the
    // bet to the slip. Idempotent — runs once per `special` id.
    useEffect(() => {
        const specialId = searchParams.get('special');
        if (!specialId || dailySpecialAppliedRef.current === specialId) return;

        const wantedSportKey = searchParams.get('sport');
        const wantedEventId  = searchParams.get('event');
        const wantedMarket   = searchParams.get('market') || 'h2h';
        const wantedOutcome  = searchParams.get('outcome');
        const wantedPoint    = searchParams.get('point');

        // Step 1: switch to the right sport if not already there
        if (wantedSportKey && selectedSport?.key !== wantedSportKey) {
            const allSports = Object.values(sports).flat();
            const target = allSports.find(s => s.key === wantedSportKey);
            if (target) {
                setSelectedSport(target);
                setActiveMarket(wantedMarket === 'totals' ? 'totals' : wantedMarket === 'spreads' ? 'spreads' : 'h2h');
                return; // wait for events to load on next render
            }
        }

        // Step 2: locate event + outcome (events must be loaded)
        if (!events.length) return;
        const ev = events.find(e => e.id === wantedEventId);
        if (!ev) return;

        const mkt = ev.markets?.[wantedMarket];
        const outcome = mkt?.outcomes?.find(o =>
            o.name === wantedOutcome &&
            (wantedPoint == null || String(o.point) === wantedPoint)
        );
        if (!outcome) return;

        // Step 3: add to slip if not already there
        const key = `${ev.id}_${wantedMarket}_${outcome.name}`;
        if (!betSlip.find(b => b.key === key)) {
            addToBetSlip({ ...ev, sport_key: ev.sport_key || wantedSportKey }, wantedMarket, outcome);
        } else {
            setShowBetSlip(true);
        }

        // Mark applied + strip params so a reload doesn't re-trigger the add
        dailySpecialAppliedRef.current = specialId;
        toast.success(t('daily_special.added_toast'));
        const next = new URLSearchParams(searchParams);
        ['special', 'event', 'sport', 'market', 'outcome', 'point'].forEach(k => next.delete(k));
        setSearchParams(next, { replace: true });
    }, [searchParams, sports, selectedSport, events, betSlip, addToBetSlip, setSearchParams]);

    // Place bets
    const placeBets = async () => {
        if (betSlip.length === 0) return;
        setPlacingBet(true);
        try {
            if (betMode === 'parlay' && betSlip.length >= 2) {
                // Place as parlay
                const amt = parseFloat(parlayAmount) || 10;
                const { data } = await axios.post(`${API}/sports/parlay`, {
                    legs: betSlip.map(b => ({
                        event_id: b.event_id, sport_key: b.sport_key,
                        market: b.market, outcome_name: b.outcome_name,
                        description: b.description || null,
                        odds: b.odds, point: b.point, amount: amt,
                        home_team: b.home_team, away_team: b.away_team,
                        commence_time: b.commence_time,
                    })),
                    total_amount: amt,
                }, { withCredentials: true });
                toast.success(`${data?.is_sgp ? 'Same Game Parlay' : 'Parlay'} placed! (${betSlip.length} legs)`);
                fireBetConfetti();
                announcePotContribution(data?.pot_contribution);
            } else {
                // Place as individual singles
                let aggregate = null;
                for (const bet of betSlip) {
                    const amount = parseFloat(betAmounts[bet.key]) || 10;
                    const { data } = await axios.post(`${API}/sports/bet`, {
                        event_id: bet.event_id, sport_key: bet.sport_key,
                        market: bet.market, outcome_name: bet.outcome_name,
                        description: bet.description || null,
                        odds: bet.odds, point: bet.point, amount,
                        home_team: bet.home_team, away_team: bet.away_team,
                        commence_time: bet.commence_time,
                    }, { withCredentials: true });
                    const c = data?.pot_contribution;
                    if (c) {
                        aggregate = aggregate
                            ? {
                                grand_add: aggregate.grand_add + (c.grand_add || 0),
                                super_add: aggregate.super_add + (c.super_add || 0),
                                blake_add: aggregate.blake_add + (c.blake_add || 0),
                                total_add: aggregate.total_add + (c.total_add || 0),
                                super_active: aggregate.super_active || c.super_active,
                            }
                            : { ...c };
                    }
                }
                toast.success(`${betSlip.length} bet(s) placed!`);
                fireBetConfetti();
                announcePotContribution(aggregate);
            }
            clearBetSlip();
            refreshUser();
            toast('Heads up — before any win is paid out, each result is reviewed and confirmed by our team.', {
                icon: <ShieldCheck className="w-4 h-4 text-[#39FF14]" />,
                duration: 8000,
                description: 'Payouts on winning tickets are credited after results are verified.',
            });
        } catch (e) {
            const detail = e.response?.data?.detail;
            if (detail && typeof detail === 'object' && detail.code) {
                // Live line-move / suspension from the server — update the slip and re-prompt.
                const oName = detail.outcome_name;
                if (detail.code === 'odds_changed' && detail.new_odds != null) {
                    setBetSlip(prev => prev.map(b => (b.outcome_name === oName && b.market === detail.market)
                        ? { ...b, odds: detail.new_odds, ...(detail.new_point != null ? { point: detail.new_point } : {}) } : b));
                    setLiveSlipStatus(prev => ({ ...prev }));
                }
                toast.error(detail.message || 'Live odds changed — please review your slip', { duration: 6000 });
            } else {
                toast.error(detail || 'Failed to place bet');
            }
        }
        setPlacingBet(false);
    };

    // Cash out a bet (live-aware: sends the quoted amount so the server can re-quote if the live value drifted)
    const cashOutBet = async (betId) => {
        const q = cashoutQuotes[betId];
        try {
            const body = q?.live ? { expected_amount: q.amount } : {};
            const { data } = await axios.post(`${API}/sports/bets/${betId}/cashout`, body, { withCredentials: true });
            toast.success(data.message);
            fireBetConfetti();
            refreshUser();
            fetchMyBets();
        } catch (e) {
            const d = e.response?.data?.detail;
            if (d && typeof d === 'object' && d.code === 'cashout_requote') {
                setCashoutQuotes(prev => ({ ...prev, [betId]: { ...(prev[betId] || {}), amount: d.new_amount, available: true, live: true } }));
                toast.error(d.message, { duration: 6000 });
            } else {
                toast.error((d && typeof d === 'object' ? d.message : d) || 'Cash out failed');
            }
        }
    };

    // Current cash-out offers for pending bets (live offers move with the odds)
    const fetchCashoutQuotes = async (bets) => {
        const pend = (bets || []).filter(b => b.status === 'pending');
        const entries = await Promise.all(pend.map(async (b) => {
            try {
                const { data } = await axios.get(`${API}/sports/bets/${b.bet_id}/cashout-quote`, { withCredentials: true });
                return [b.bet_id, data];
            } catch { return [b.bet_id, null]; }
        }));
        setCashoutQuotes(Object.fromEntries(entries.filter(e => e[1])));
    };

    // Fetch my bets
    const fetchMyBets = async () => {
        try {
            const { data } = await axios.get(`${API}/sports/bets`, { withCredentials: true });
            setMyBets(data.bets || []);
            fetchCashoutQuotes(data.bets || []);
            // Fetch live scores for pending bets
            try {
                const { data: scoresData } = await axios.get(`${API}/sports/live/my-bets-scores`, { withCredentials: true });
                setBetScores(scoresData.scores || {});
            } catch {}
        } catch (e) { console.error(e); }
    };

    // Featured live game — real live event, refreshed for the hero card.
    useEffect(() => {
        let alive = true;
        const pull = async () => {
            try {
                const { data } = await axios.get(`${API}/sports/live/odds`, { withCredentials: true });
                if (alive) setFeatured((data.events || [])[0] || null);
            } catch { /* featured is optional */ }
        };
        pull();
        const id = setInterval(pull, 20000);
        return () => { alive = false; clearInterval(id); };
    }, []);

    const isInSlip = (eventId, market, outcomeName) => {
        return betSlip.some(b => b.key === `${eventId}_${market}_${outcomeName}`);
    };

    const fetchCashoutHistory = async () => {
        try {
            const { data } = await axios.get(`${API}/sports/cashout-history`, { withCredentials: true });
            setCashoutHistory(data);
        } catch { /* ignore */ }
    };

    // Refresh live cash-out offers every 10s while the My Bets drawer is open
    useEffect(() => {
        if (!showMyBets) return;
        const id = setInterval(() => { fetchMyBets(); }, 10000);
        return () => clearInterval(id);
    }, [showMyBets]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-[#FFD700] animate-spin" />
            </div>
        );
    }

    return (
        <div className="a11y-font min-h-screen bg-[#050505]">
            <Header />
            {/* Community pots (Community Grand / BEToBuck$) removed from UI (Jun 2026) */}
            {/* <JackpotPromoBanner /> */}
            <div className="max-w-7xl mx-auto px-4 py-6">
                {/* Daily Special — featured bet curated by admin (auto-hides when none live) */}
                <div className="mb-5">
                    <DailySpecialBanner />
                </div>
                {/* SPORTSBOOK hero over stadium bg */}
                <div className="relative overflow-hidden rounded-2xl mb-4" data-testid="sportsbook-hero">
                    <img src={`${SB}/hero_stadium.png`} alt="" width="1200" height="300" loading="eager"
                        className="absolute inset-0 w-full h-full object-cover object-right" />
                    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
                    <div className="relative px-5 py-6 sm:py-8">
                        <h1 className="text-4xl sm:text-5xl font-black italic tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-[#FFE68A] via-[#FFD700] to-[#B8860B] drop-shadow"
                            style={{ fontStretch: 'condensed' }}>SPORTSBOOK</h1>
                        <div className="h-1 w-24 mt-2 rounded-full bg-gradient-to-r from-[#FFD700] to-transparent" />
                    </div>
                </div>

                {/* Segmented nav: LIVE | UPCOMING | MY BETS */}
                <div className="grid grid-cols-3 gap-2 mb-4" data-testid="sb-segmented">
                    {[
                        { id: 'live', label: 'LIVE', active: isLiveMode },
                        { id: 'upcoming', label: 'UPCOMING', active: !isLiveMode },
                        { id: 'mybets', label: 'MY BETS', active: false },
                    ].map(seg => (
                        <button key={seg.id} data-testid={`seg-${seg.id}`}
                            onClick={() => {
                                if (seg.id === 'live') { setActiveMarket('h2h'); setSelectedSport({ key: '__live__', title: 'Live Now' }); }
                                else if (seg.id === 'upcoming') { const pop = Object.values(sports).flat().filter(s => s.featured && !s.has_outrights); if (isLiveMode) setSelectedSport(pop[0] || null); }
                                else { fetchMyBets(); setShowMyBets(true); }
                            }}
                            className={`h-11 rounded-xl text-sm font-black tracking-wide transition-all flex items-center justify-center gap-1.5 border ${
                                seg.active
                                    ? seg.id === 'live' ? 'bg-black text-[#FF3B30] border-[#FF3B30]' : 'bg-[#FFD700] text-black border-[#FFD700]'
                                    : 'bg-[#111] text-[#A0AAB2] border-white/10 hover:text-white'
                            }`}>
                            {seg.id === 'live' && <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3B30] opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF3B30]" /></span>}
                            {seg.id === 'mybets' && <Receipt className="w-3.5 h-3.5" />}
                            {seg.label}
                        </button>
                    ))}
                </div>

                {/* Live Scores Ticker */}
                <LiveScoresTicker />

                {/* Featured Live Game — real live event */}
                {featured && (
                    <div className="relative overflow-hidden rounded-2xl mb-4 border border-[#FFD700]/20" data-testid="featured-live-card">
                        <img src={(featured.sport_key === 'baseball_mlb') ? `${SB}/featured_baseball.png` : `${SB}/hero_stadium.png`} alt=""
                            width="1200" height="400" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/[0.62]" />
                        <div className="relative p-4">
                            <div className="flex items-center justify-between mb-3">
                                <span className="inline-flex items-center gap-1.5 text-[11px] font-black text-white bg-[#FF3B30] px-2 py-0.5 rounded">
                                    <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-white" /></span>
                                    FEATURED LIVE
                                </span>
                                <span className="text-xs font-bold text-white/80">{featured.sport_title || 'Live'}</span>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                                {[{ name: featured.away_team, score: featured.away_score }, { name: featured.home_team, score: featured.home_score }].map((t, ti) => (
                                    <React.Fragment key={ti}>
                                        {ti === 1 && <div className="text-center px-2"><span className="text-3xl font-black text-white font-mono">{featured.away_score ?? 0}<span className="text-white/40 mx-1">–</span>{featured.home_score ?? 0}</span></div>}
                                        <div className="flex-1 flex flex-col items-center gap-1.5 min-w-0">
                                            <span className="scale-150"><TeamBadge teamName={t.name} sportKey={featured.sport_key} /></span>
                                            <span className="text-xs font-bold text-white text-center leading-tight">{t.name}</span>
                                        </div>
                                    </React.Fragment>
                                ))}
                            </div>
                            {featured.markets?.h2h?.outcomes && !featured.suspended && (
                                <div className="grid grid-cols-2 gap-2 mt-3">
                                    {featured.markets.h2h.outcomes.slice(0, 2).map((o, oi) => (
                                        <button key={oi} onClick={() => addToBetSlip(featured, 'h2h', o)}
                                            data-testid={`featured-odds-${o.name}`}
                                            className={`h-11 rounded-lg font-mono font-black text-sm border transition-all ${
                                                isInSlip(featured.id, 'h2h', o.name) ? 'bg-[#FFD700] text-black border-[#FFD700]'
                                                    : `bg-black/50 border-white/15 ${o.price > 0 ? 'text-[#39FF14]' : 'text-[#FF6B6B]'} hover:border-[#FFD700]/60`}`}>
                                            {formatOdds(o.price)}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Horizontal sport selector + MORE drawer trigger */}
                <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 mb-4" data-testid="sport-chip-row">
                    {[
                        { key: 'baseball_mlb', label: 'MLB' },
                        { key: 'americanfootball_nfl', label: 'NFL' },
                        { key: 'americanfootball_ncaaf', label: 'NCAAF' },
                        { key: 'basketball_nba', label: 'NBA' },
                        { key: 'boxing_boxing', label: 'BOXING' },
                    ].map(chip => {
                        const found = Object.values(sports).flat().find(s => s.key === chip.key) || chip;
                        const active = selectedSport?.key === chip.key;
                        return (
                            <button key={chip.key} data-testid={`chip-${chip.key}`}
                                onClick={() => { setActiveMarket('h2h'); setSelectedSport({ key: found.key, title: found.title || chip.label }); }}
                                className={`shrink-0 min-w-[72px] h-16 rounded-xl border flex flex-col items-center justify-center gap-1 text-[11px] font-black transition-all ${
                                    active ? 'bg-[#FFD700]/10 border-[#FFD700] text-[#FFD700]' : 'bg-[#111] border-white/10 text-[#A0AAB2] hover:text-white'}`}>
                                <SportGlyph label={chip.label} sportKey={chip.key} active={active} />
                                {chip.label}
                            </button>
                        );
                    })}
                    <button data-testid="chip-more" onClick={() => setMoreOpen(true)}
                        className="shrink-0 min-w-[72px] h-16 rounded-xl border border-white/10 bg-[#111] text-[#A0AAB2] hover:text-white flex flex-col items-center justify-center gap-1 text-[11px] font-black">
                        <MoreHorizontal className="w-5 h-5" />
                        MORE
                    </button>
                </div>


                {/* Sport Hero Banner (replaced by SPORTSBOOK hero above) */}
                {false && selectedSport && (
                    <div className="relative overflow-hidden rounded-2xl h-40 sm:h-48 mb-2" data-testid="sport-hero-banner">
                        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-transparent z-10" />
                        <img
                            src={(() => {
                                const key = selectedSport.key || '';
                                const sportImages = {
                                    'americanfootball_nfl': 'https://static.prod-images.emergentagent.com/jobs/0c54b792-bce9-4530-83d8-e1cf662828bf/images/677de6e7fd14c453da2731a292c46dd2354f082eeeea8bafe94f7fc8587cca2b.png',
                                    'americanfootball_ncaaf': 'https://static.prod-images.emergentagent.com/jobs/0c54b792-bce9-4530-83d8-e1cf662828bf/images/677de6e7fd14c453da2731a292c46dd2354f082eeeea8bafe94f7fc8587cca2b.png',
                                    'basketball_nba': 'https://static.prod-images.emergentagent.com/jobs/0c54b792-bce9-4530-83d8-e1cf662828bf/images/0e7d66168f22c8fced03222114a4ab313286082c76e8fc194b025c5e1556f167.png',
                                    'basketball_ncaab': 'https://static.prod-images.emergentagent.com/jobs/0c54b792-bce9-4530-83d8-e1cf662828bf/images/0e7d66168f22c8fced03222114a4ab313286082c76e8fc194b025c5e1556f167.png',
                                    'baseball_mlb': 'https://static.prod-images.emergentagent.com/jobs/0c54b792-bce9-4530-83d8-e1cf662828bf/images/ffc560e54616617cf1a73d91c4524e7e1f880c5bf23e226e35a86bc0e27d5254.png',
                                    'icehockey_nhl': 'https://static.prod-images.emergentagent.com/jobs/0c54b792-bce9-4530-83d8-e1cf662828bf/images/48e2f2503cad0bafc1b91456fd2c3df61abb79460a59edcf1e115e4a8f6267b3.png',
                                    'soccer_epl': 'https://images.pexels.com/photos/14741747/pexels-photo-14741747.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
                                    'soccer_germany_bundesliga': 'https://images.pexels.com/photos/14741747/pexels-photo-14741747.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
                                    'soccer_italy_serie_a': 'https://images.pexels.com/photos/14741747/pexels-photo-14741747.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
                                    'soccer_spain_la_liga': 'https://images.pexels.com/photos/14741747/pexels-photo-14741747.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
                                    'soccer_uefa_champs_league': 'https://images.pexels.com/photos/14741747/pexels-photo-14741747.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
                                    'mma_mixed_martial_arts': 'https://images.pexels.com/photos/28550403/pexels-photo-28550403.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
                                    'boxing_boxing': 'https://images.unsplash.com/photo-1575747503976-5e6e96a13504?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
                                };
                                // Match by prefix
                                if (sportImages[key]) return sportImages[key];
                                if (key.startsWith('americanfootball')) return sportImages['americanfootball_nfl'];
                                if (key.startsWith('basketball')) return sportImages['basketball_nba'];
                                if (key.startsWith('baseball')) return sportImages['baseball_mlb'];
                                if (key.startsWith('icehockey')) return sportImages['icehockey_nhl'];
                                if (key.startsWith('soccer')) return sportImages['soccer_epl'];
                                if (key.startsWith('mma')) return sportImages['mma_mixed_martial_arts'];
                                if (key.startsWith('boxing')) return sportImages['boxing_boxing'];
                                return sportImages['americanfootball_nfl'];
                            })()}
                            alt={selectedSport.title}
                            className="absolute inset-0 w-full h-full object-cover object-top"
                        />
                        <div className="relative z-20 h-full flex flex-col justify-center px-6 sm:px-8">
                            <h2 className="text-2xl sm:text-3xl font-black text-white drop-shadow-lg">{selectedSport.title}</h2>
                            <p className="text-sm text-[#A0AAB2] mt-1">{events.length} {events.length === 1 ? 'game' : 'games'} available</p>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[#050505] to-transparent z-10" />
                    </div>
                )}

                <div className="max-w-3xl lg:max-w-6xl mx-auto pb-28 lg:flex lg:gap-6 lg:items-start">
                    {/* Sports Sidebar (replaced by chip row + MORE drawer) */}
                    <div className="hidden">
                        <div className="card-dark p-3 lg:sticky lg:top-4 max-h-[70vh] overflow-y-auto scrollbar-hide">
                            <div className="relative mb-3">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A0AAB2]" />
                                <input
                                    type="text"
                                    placeholder="Search sports..."
                                    value={sportSearch}
                                    onChange={e => { setSportSearch(e.target.value); if (e.target.value) setShowAllSports(true); }}
                                    className="w-full pl-8 pr-3 py-1.5 rounded-lg bg-[#0a0a0a] border border-white/10 text-white text-xs placeholder:text-[#A0AAB2]/50 focus:outline-none focus:border-[#FFD700]/50"
                                    data-testid="sport-search"
                                />
                            </div>

                            {/* Live Now — dedicated in-play choice (all sports, games underway) */}
                            {!sportSearch && (
                                <button
                                    onClick={() => { setActiveMarket('h2h'); setSelectedSport({ key: '__live__', title: 'Live Now' }); }}
                                    className={`w-full text-left px-3 py-2.5 mb-3 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${
                                        isLiveMode
                                            ? 'bg-[#FF3B30] text-white'
                                            : 'bg-[#FF3B30]/10 text-[#FF3B30] hover:bg-[#FF3B30]/20 border border-[#FF3B30]/30'
                                    }`}
                                    data-testid="sport-live-now"
                                >
                                    <span className="relative flex h-2.5 w-2.5">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75" />
                                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-current" />
                                    </span>
                                    Live Now
                                    <span className="ml-auto text-[10px] font-normal opacity-80">In-play</span>
                                </button>
                            )}

                            {/* Popular Sports */}
                            {!sportSearch && (
                                <div className="mb-3">
                                    <p className="text-[10px] font-bold text-[#FFD700] uppercase tracking-wider px-2 mb-1">Popular</p>
                                    {Object.values(sports).flat().filter(s => s.featured && !s.has_outrights).map(s => (
                                        <button
                                            key={s.key}
                                            onClick={() => setSelectedSport(s)}
                                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center justify-between ${
                                                selectedSport?.key === s.key
                                                    ? 'bg-[#FFD700]/10 text-[#FFD700] font-bold'
                                                    : 'text-[#A0AAB2] hover:text-white hover:bg-white/5'
                                            }`}
                                            data-testid={`sport-${s.key}`}
                                        >
                                            <span className="truncate">{s.title}</span>
                                            {selectedSport?.key === s.key && <ChevronRight className="w-3 h-3 shrink-0" />}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* All Sports — collapsible */}
                            {!sportSearch && (
                                <button
                                    onClick={() => setShowAllSports(!showAllSports)}
                                    className="w-full flex items-center justify-between px-2 py-1.5 mb-1 text-[10px] font-bold text-[#A0AAB2] uppercase tracking-wider hover:text-white transition-colors"
                                    data-testid="toggle-all-sports"
                                >
                                    All Sports
                                    <ChevronDown className={`w-3 h-3 transition-transform ${showAllSports ? 'rotate-180' : ''}`} />
                                </button>
                            )}

                            {(showAllSports || sportSearch) && Object.entries(sports).map(([group, sportsList]) => {
                                const filtered = sportsList.filter(s => !s.has_outrights && (!sportSearch || s.title.toLowerCase().includes(sportSearch.toLowerCase()) || group.toLowerCase().includes(sportSearch.toLowerCase())));
                                if (filtered.length === 0) return null;
                                return (
                                <div key={group} className="mb-2">
                                    <p className="text-[10px] font-bold text-[#A0AAB2] uppercase tracking-wider px-2 py-1">{group}</p>
                                    {filtered.map(s => (
                                        <button
                                            key={s.key}
                                            onClick={() => setSelectedSport(s)}
                                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center justify-between ${
                                                selectedSport?.key === s.key
                                                    ? 'bg-[#FFD700]/10 text-[#FFD700] font-bold'
                                                    : 'text-[#A0AAB2] hover:text-white hover:bg-white/5'
                                            }`}
                                            data-testid={`sport-${s.key}`}
                                        >
                                            <span className="truncate">{s.title}</span>
                                            {selectedSport?.key === s.key && <ChevronRight className="w-3 h-3 shrink-0" />}
                                        </button>
                                    ))}
                                </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Events */}
                    <div className="flex-1 min-w-0">
                        {selectedSport && (
                            <>
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-lg font-bold text-white">{selectedSport.title}</h2>
                                    <div className="flex gap-1 flex-wrap">
                                        {(isLiveMode ? ['h2h', 'spreads', 'totals'] : ['h2h', 'spreads', 'totals', 'props']).map(m => (
                                            <button
                                                key={m}
                                                onClick={() => setActiveMarket(m)}
                                                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                                                    activeMarket === m
                                                        ? m === 'props' ? 'bg-[#A855F7] text-white' : 'bg-[#FFD700] text-black'
                                                        : 'bg-white/5 text-[#A0AAB2] hover:text-white'
                                                }`}
                                                data-testid={`market-${m}`}
                                            >
                                                {m === 'h2h' ? 'Moneyline' : m === 'spreads' ? 'Spread' : m === 'totals' ? 'Total' : 'Props'}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {activeMarket === 'props' ? (
                                    /* Props View */
                                    propsLoading ? (
                                        <div className="flex items-center justify-center py-20">
                                            <Loader2 className="w-6 h-6 text-[#A855F7] animate-spin" />
                                        </div>
                                    ) : selectedPropsEvent ? (
                                        /* Show props for selected event */
                                        <div>
                                            <button
                                                onClick={() => { setSelectedPropsEvent(null); setPropsData([]); }}
                                                className="text-sm text-[#A855F7] hover:text-[#A855F7]/80 mb-4 flex items-center gap-1"
                                                data-testid="props-back-btn"
                                            >
                                                <ChevronRight className="w-3 h-3 rotate-180" /> Back to games
                                            </button>
                                            <div className="card-dark p-4 mb-4">
                                                <p className="text-white font-bold">{selectedPropsEvent.away_team} @ {selectedPropsEvent.home_team}</p>
                                                <p className="text-xs text-[#A0AAB2]">{formatTime(selectedPropsEvent.commence_time)}</p>
                                            </div>
                                            {propsData.length === 0 ? (
                                                <p className="text-[#A0AAB2] text-center py-12">No player props available for this game yet</p>
                                            ) : (
                                                <div className="space-y-4">
                                                    {propsData.map(prop => (
                                                        <div key={prop.key} className="card-dark p-4">
                                                            <div className="flex items-center justify-between mb-3">
                                                                <h3 className="text-sm font-bold text-[#A855F7]">{prop.label}</h3>
                                                                <span className="text-[10px] text-[#666]">{prop.bookmaker}</span>
                                                            </div>
                                                            <div className="space-y-2">
                                                                {/* Group outcomes by player (description) */}
                                                                {Object.entries(
                                                                    prop.outcomes.reduce((acc, o) => {
                                                                        const player = o.description || 'Unknown';
                                                                        if (!acc[player]) acc[player] = [];
                                                                        acc[player].push(o);
                                                                        return acc;
                                                                    }, {})
                                                                ).map(([player, outcomes]) => (
                                                                    <div key={player} className="flex items-center gap-3 py-1.5 border-b border-white/5 last:border-0">
                                                                        <span className="flex-1 text-sm text-white min-w-0 truncate">{player}</span>
                                                                        <div className="flex gap-2 shrink-0">
                                                                            {outcomes.map((o, oi) => (
                                                                                <button
                                                                                    key={oi}
                                                                                    onClick={() => addToBetSlip(
                                                                                        { ...selectedPropsEvent, sport_key: selectedSport.key },
                                                                                        prop.key,
                                                                                        { ...o, name: `${player} ${o.name}${o.point !== undefined ? ' ' + o.point : ''}` }
                                                                                    )}
                                                                                    className={`px-2.5 py-1.5 rounded-lg text-center min-w-[65px] transition-all border text-xs ${
                                                                                        isInSlip(selectedPropsEvent.id, prop.key, `${player} ${o.name}${o.point !== undefined ? ' ' + o.point : ''}`)
                                                                                            ? 'bg-[#A855F7]/20 border-[#A855F7] text-[#A855F7]'
                                                                                            : 'bg-white/5 border-white/10 text-white hover:bg-white/10'
                                                                                    }`}
                                                                                    data-testid={`prop-btn-${player}-${o.name}`}
                                                                                >
                                                                                    <span className="text-[10px] text-[#A0AAB2] block">{o.name} {o.point !== undefined ? o.point : ''}</span>
                                                                                    <span className={`font-mono font-bold ${o.price > 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                                                                        {formatOdds(o.price)}
                                                                                    </span>
                                                                                </button>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        /* Show events list for props */
                                        propsEvents.length === 0 ? (
                                            <div className="text-center py-20">
                                                <p className="text-[#A0AAB2]">No upcoming games with props available</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                {propsEvents.map(event => (
                                                    <button
                                                        key={event.id}
                                                        onClick={() => fetchEventProps(selectedSport, event.id)}
                                                        className="w-full card-dark p-4 text-left hover:bg-white/5 transition-all"
                                                        data-testid={`props-event-${event.id}`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <p className="text-sm font-bold text-white">{event.away_team} @ {event.home_team}</p>
                                                                <p className="text-xs text-[#A0AAB2] mt-1">{formatTime(event.commence_time)}</p>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs px-2 py-1 rounded-full bg-[#A855F7]/20 text-[#A855F7] font-bold">Props</span>
                                                                <ChevronRight className="w-4 h-4 text-[#A0AAB2]" />
                                                            </div>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )
                                    )
                                ) : (
                                /* Regular markets view */
                                eventsLoading ? (
                                    <div className="flex items-center justify-center py-20">
                                        <Loader2 className="w-6 h-6 text-[#FFD700] animate-spin" />
                                    </div>
                                ) : events.length === 0 ? (
                                    <div className="text-center py-20 space-y-3">
                                        <p className="text-[#A0AAB2]">
                                            {isLiveMode
                                                ? 'No games are live right now. Check back when games are underway.'
                                                : fetchError && fetchError !== 'no_events'
                                                ? `Error loading events: ${fetchError}`
                                                : 'No events available for this sport right now'}
                                        </p>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => fetchOdds(selectedSport)}
                                            className="border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/10"
                                            data-testid="retry-odds-btn"
                                        >
                                            Retry
                                        </Button>
                                    </div>
                                ) : liveFilter && !events.some(e => new Date(e.commence_time) <= new Date()) ? (
                                    <div className="text-center py-20 space-y-3">
                                        <p className="text-[#A0AAB2]">No live games right now for {selectedSport?.title}</p>
                                        <Button variant="outline" size="sm" onClick={() => setLiveFilter(false)}
                                            className="border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10">
                                            Show All Games
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {events
                                            .filter(e => !liveFilter || new Date(e.commence_time) <= new Date())
                                            .map(event => {
                                            const market = event.markets?.[activeMarket];
                                            const isLive = new Date(event.commence_time) <= new Date();
                                            if (!market) return null;
                                            return (
                                                <div key={event.id} className={`card-dark p-4 ${isLive ? 'border-l-2 border-l-[#FF3B30]' : ''}`} data-testid={`event-${event.id}`}>
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div className="flex items-center gap-2">
                                                            {isLive ? (
                                                                <span className="flex items-center gap-1 text-xs font-bold text-[#FF3B30]">
                                                                    <span className="relative flex h-2 w-2">
                                                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3B30] opacity-75" />
                                                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF3B30]" />
                                                                    </span>
                                                                    LIVE
                                                                    {(event.home_score != null && event.away_score != null) && (
                                                                        <span className="ml-1 text-white font-mono" data-testid={`live-score-inline-${event.id}`}>
                                                                            {event.away_score}–{event.home_score}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            ) : (
                                                                <>
                                                                    <Clock className="w-3 h-3 text-[#A0AAB2]" />
                                                                    <span className="text-xs text-[#A0AAB2]">{formatTime(event.commence_time)}</span>
                                                                </>
                                                            )}
                                                        </div>
                                                        <span className="text-[10px] text-[#666]">{event.bookmaker}</span>
                                                    </div>

                                                    {/* Matchup + Odds */}
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2">
                                                                <TeamBadge teamName={event.away_team} sportKey={event.sport_key} />
                                                                <p className="text-sm font-bold text-white truncate">{event.away_team}</p>
                                                            </div>
                                                            <div className="flex items-center gap-2 mt-1.5">
                                                                <TeamBadge teamName={event.home_team} sportKey={event.sport_key} />
                                                                <p className="text-sm font-bold text-white truncate">{event.home_team}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-2 shrink-0">
                                                            {market.stale ? (
                                                                <span className="px-3 py-2 rounded-lg text-center text-[11px] font-bold text-[#FF3B30] border border-[#FF3B30]/40 bg-[#FF3B30]/10" data-testid={`market-suspended-${event.id}`}>
                                                                    SUSPENDED
                                                                </span>
                                                            ) : market.outcomes.map((outcome, oi) => (
                                                                <button
                                                                    key={oi}
                                                                    onClick={() => addToBetSlip(event, activeMarket, outcome)}
                                                                    className={`px-3 py-2 rounded-lg text-center min-w-[70px] transition-all border ${
                                                                        isInSlip(event.id, activeMarket, outcome.name)
                                                                            ? 'bg-[#39FF14]/20 border-[#39FF14] text-[#39FF14]'
                                                                            : 'bg-white/5 border-white/10 text-white hover:bg-white/10'
                                                                    }`}
                                                                    data-testid={`odds-btn-${event.id}-${outcome.name}`}
                                                                >
                                                                    {outcome.point !== undefined && (
                                                                        <span className="text-[10px] text-[#A0AAB2] block">
                                                                            {outcome.point > 0 ? '+' : ''}{outcome.point}
                                                                        </span>
                                                                    )}
                                                                    <span className={`text-sm font-mono font-bold ${outcome.price > 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                                                        {formatOdds(outcome.price)}
                                                                    </span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    {isLive && isLiveMode && (
                                                        <LiveExtraMarkets
                                                            event={event}
                                                            state={liveExtra[event.id]}
                                                            onToggle={() => toggleLiveExtra(event)}
                                                            addToBetSlip={addToBetSlip}
                                                            isInSlip={isInSlip}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )
                                )}
                            </>
                        )}
                    </div>

                    {/* Bet Slip (Desktop) */}
                    {betSlip.length > 0 && (
                        <div className="hidden lg:block w-80 shrink-0 lg:sticky lg:top-4 lg:self-start">
                            <BetSlipPanel
                                user={user}
                                betSlip={betSlip}
                                betAmounts={betAmounts}
                                setBetAmounts={setBetAmounts}
                                removeBet={removeBet}
                                clearBetSlip={clearBetSlip}
                                placeBets={placeBets}
                                placingBet={placingBet}
                                betMode={betMode}
                                setBetMode={setBetMode}
                                parlayAmount={parlayAmount}
                                setParlayAmount={setParlayAmount}
                                limits={limits}
                                liveSlipStatus={liveSlipStatus}
                                acceptNewOdds={acceptNewOdds}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Mobile Bet Slip Drawer */}
            <AnimatePresence>
                {showBetSlip && (
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 25 }}
                        className="fixed inset-x-0 bottom-0 z-50 lg:hidden max-h-[80vh] overflow-y-auto bg-[#121212] border-t border-white/10 rounded-t-2xl"
                    >
                        <div className="p-4">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-lg font-bold text-white">Bet Slip ({betSlip.length})</h3>
                                <Button variant="ghost" size="icon" onClick={() => setShowBetSlip(false)}>
                                    <X className="w-5 h-5 text-[#A0AAB2]" />
                                </Button>
                            </div>
                            <BetSlipPanel
                                user={user}
                                betSlip={betSlip}
                                betAmounts={betAmounts}
                                setBetAmounts={setBetAmounts}
                                removeBet={removeBet}
                                clearBetSlip={clearBetSlip}
                                placeBets={placeBets}
                                placingBet={placingBet}
                                betMode={betMode}
                                setBetMode={setBetMode}
                                parlayAmount={parlayAmount}
                                setParlayAmount={setParlayAmount}
                                limits={limits}
                                liveSlipStatus={liveSlipStatus}
                                acceptNewOdds={acceptNewOdds}
                                mobile
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Sticky collapsed bet-slip bar (mobile) */}
            {betSlip.length > 0 && !showBetSlip && (() => {
                const amts = betSlip.map(b => parseFloat(betAmounts[b.key] || 0));
                let potential = null;
                if (betMode === 'parlay' && betSlip.length >= 2) {
                    const stake = parseFloat(parlayAmount || 0);
                    if (stake > 0) potential = stake * betSlip.reduce((acc, b) => acc * (b.odds > 0 ? b.odds / 100 + 1 : 100 / Math.abs(b.odds) + 1), 1);
                } else if (amts.some(a => a > 0)) {
                    potential = betSlip.reduce((s, b, i) => s + (amts[i] > 0 ? parseFloat(calcPayout(amts[i], b.odds)) : 0), 0);
                }
                return (
                    <div className="fixed inset-x-0 bottom-0 z-40 lg:hidden px-3" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 10px)' }}>
                        <button onClick={() => setShowBetSlip(true)} data-testid="sticky-betslip-bar"
                            className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 bg-gradient-to-r from-[#B8860B] via-[#FFD700] to-[#B8860B] text-black shadow-2xl border border-[#FFD700]">
                            <span className="relative">
                                <Ticket className="w-6 h-6" />
                                <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-black text-[#FFD700] text-[10px] font-black flex items-center justify-center">{betSlip.length}</span>
                            </span>
                            <div className="text-left leading-tight">
                                <div className="text-sm font-black tracking-wide">BET SLIP · {betSlip.length} PICK{betSlip.length === 1 ? '' : 'S'}</div>
                                <div className="text-[11px] font-bold opacity-70">VIEW PICKS</div>
                            </div>
                            <div className="ml-auto text-right leading-tight">
                                <div className="text-[10px] font-black opacity-70 uppercase">Potential Win</div>
                                <div className="text-lg font-black font-mono">{potential != null ? `$${potential.toFixed(2)}` : '—'}</div>
                            </div>
                            <ChevronRight className="w-6 h-6" />
                        </button>
                    </div>
                );
            })()}

            {/* MORE — All Sports searchable drawer */}
            <AnimatePresence>
                {moreOpen && (
                    <motion.div className="fixed inset-0 z-50 flex items-end" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <div className="absolute inset-0 bg-black/70" onClick={() => setMoreOpen(false)} />
                        <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', damping: 28 }}
                            className="relative w-full max-h-[82vh] overflow-y-auto bg-[#111] border-t border-white/10 rounded-t-2xl p-4" data-testid="all-sports-drawer">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-lg font-black text-white">All Sports</h3>
                                <button onClick={() => setMoreOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10"><X className="w-5 h-5 text-[#A0AAB2]" /></button>
                            </div>
                            <div className="relative mb-4">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0AAB2]" />
                                <input type="text" placeholder="Search sports…" value={sportSearch} onChange={e => setSportSearch(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-[#0a0a0a] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FFD700]/50"
                                    data-testid="all-sports-search" />
                            </div>
                            {Object.entries(sports).map(([group, list]) => {
                                const filtered = list.filter(s => !s.has_outrights && (!sportSearch || s.title.toLowerCase().includes(sportSearch.toLowerCase()) || group.toLowerCase().includes(sportSearch.toLowerCase())));
                                if (filtered.length === 0) return null;
                                return (
                                    <div key={group} className="mb-3">
                                        <p className="text-[10px] font-black text-[#FFD700] uppercase tracking-widest px-1 mb-1.5 flex items-center gap-1.5">
                                            <LeagueIcon sportKey={filtered[0]?.key} label={group} size={18} /> {group}
                                        </p>
                                        <div className="grid grid-cols-2 gap-2">
                                            {filtered.map(s => (
                                                <button key={s.key} data-testid={`drawer-sport-${s.key}`}
                                                    onClick={() => { setActiveMarket('h2h'); setSelectedSport(s); setSportSearch(''); setMoreOpen(false); }}
                                                    className={`text-left px-3 py-2.5 rounded-xl text-sm border transition-all ${selectedSport?.key === s.key ? 'bg-[#FFD700]/10 border-[#FFD700] text-[#FFD700] font-bold' : 'bg-[#0a0a0a] border-white/10 text-[#A0AAB2] hover:text-white'}`}>
                                                    {s.title}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* My Bets Modal */}
            <AnimatePresence>
                {showMyBets && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
                        onClick={() => setShowMyBets(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.9 }}
                            onClick={e => e.stopPropagation()}
                            className="w-full max-w-lg max-h-[80vh] overflow-y-auto card-dark p-6 rounded-2xl"
                            data-testid="my-bets-modal"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-bold text-white">My Bets</h3>
                                <Button variant="ghost" size="icon" onClick={() => setShowMyBets(false)}>
                                    <X className="w-5 h-5 text-[#A0AAB2]" />
                                </Button>
                            </div>
                            <div className="flex gap-2 mb-4">
                                <button
                                    onClick={() => setMyBetsTab('active')}
                                    className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${myBetsTab === 'active' ? 'bg-[#FFD700] text-black' : 'bg-white/5 text-[#A0AAB2] hover:bg-white/10'}`}
                                    data-testid="mybets-tab-active"
                                >
                                    Bets
                                </button>
                                <button
                                    onClick={() => { setMyBetsTab('cashouts'); fetchCashoutHistory(); }}
                                    className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${myBetsTab === 'cashouts' ? 'bg-[#00BFFF] text-black' : 'bg-white/5 text-[#A0AAB2] hover:bg-white/10'}`}
                                    data-testid="mybets-tab-cashouts"
                                >
                                    Cash-Out History
                                </button>
                            </div>
                            {myBetsTab === 'cashouts' ? (
                                cashoutHistory.rows.length === 0 ? (
                                    <p className="text-[#A0AAB2] text-center py-8" data-testid="cashout-history-empty">You haven't cashed out any bets yet.</p>
                                ) : (
                                    <div className="space-y-3" data-testid="cashout-history-list">
                                        <div className="flex items-center justify-between text-xs px-1">
                                            <span className="text-[#A0AAB2]">Total cashed out</span>
                                            <span className="font-mono font-bold text-[#00BFFF]">{Number(cashoutHistory.total_cashed_out).toFixed(2)}</span>
                                        </div>
                                        {cashoutHistory.rows.map((r, i) => (
                                            <div key={i} className="p-3 rounded-xl border border-[#00BFFF]/30 bg-[#0a0a0a]" data-testid={`cashout-history-${i}`}>
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-sm text-white font-bold truncate max-w-[70%]">{r.outcome_name}</span>
                                                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded ${r.cashout_type === 'live' ? 'bg-[#FF3B30]/15 text-[#FF3B30]' : 'bg-white/10 text-[#A0AAB2]'}`}>
                                                        {r.cashout_type === 'live' ? 'LIVE' : 'PRE'}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-[#A0AAB2] mb-2">{r.event_name}</p>
                                                {r.settled_at && (
                                                    <p className="text-[10px] text-[#A0AAB2] mb-2" data-testid={`cashout-history-${i}-date`}>
                                                        {formatDateCT(r.settled_at)}
                                                        <span className="ml-1 px-1 py-0.5 rounded bg-white/10 text-[9px] font-bold text-[#FFD700] align-middle">CT</span>
                                                    </p>
                                                )}
                                                <div className="grid grid-cols-3 gap-2 text-center">
                                                    <div><p className="text-[9px] text-[#A0AAB2] uppercase">Wager</p><p className="text-xs font-mono text-white">{r.stake.toFixed(2)}</p></div>
                                                    <div><p className="text-[9px] text-[#A0AAB2] uppercase">Cashed</p><p className="text-xs font-mono text-[#00BFFF] font-bold">{r.cashout_amount.toFixed(2)}</p></div>
                                                    <div><p className="text-[9px] text-[#A0AAB2] uppercase">Net</p><p className={`text-xs font-mono font-bold ${r.net_vs_stake >= 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>{r.net_vs_stake >= 0 ? '+' : ''}{r.net_vs_stake.toFixed(2)}</p></div>
                                                </div>
                                                {r.vs_ride !== null && (
                                                    <div className={`mt-2 text-[10px] text-center font-bold rounded py-1 ${r.vs_ride > 0 ? 'bg-[#FF3B30]/10 text-[#FF3B30]' : 'bg-[#39FF14]/10 text-[#39FF14]'}`}>
                                                        {r.vs_ride > 0
                                                            ? `Left ${r.vs_ride.toFixed(2)} on the table (would've returned ${r.would_return.toFixed(2)})`
                                                            : `Good call — saved ${Math.abs(r.vs_ride).toFixed(2)} vs letting it ride`}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )
                            ) : myBets.length === 0 ? (
                                <p className="text-[#A0AAB2] text-center py-8">No bets placed yet</p>
                            ) : (
                                <div className="space-y-3">
                                    {myBets.map((bet, i) => (
                                        <div key={i} className={`p-3 rounded-xl border bg-[#0a0a0a] ${
                                            bet.status === 'won' ? 'border-[#39FF14]/40 border-l-4 border-l-[#39FF14]' :
                                            bet.status === 'lost' ? 'border-[#FF3B30]/40 border-l-4 border-l-[#FF3B30]' :
                                            bet.status === 'cashed_out' ? 'border-[#00BFFF]/40 border-l-4 border-l-[#00BFFF]' :
                                            bet.status === 'pending' ? 'border-[#FFD700]/40 border-l-4 border-l-[#FFD700]' :
                                            'border-white/10'
                                        }`} data-testid={`my-bet-${i}`}>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                                    bet.status === 'pending' ? (awaitingConfirmation(bet) ? 'bg-[#FFA500]/20 text-[#FFA500]' : 'bg-[#FFD700]/20 text-[#FFD700]') :
                                                    bet.status === 'won' ? 'bg-[#39FF14]/20 text-[#39FF14]' :
                                                    bet.status === 'lost' ? 'bg-[#FF3B30]/20 text-[#FF3B30]' :
                                                    bet.status === 'cashed_out' ? 'bg-[#00BFFF]/20 text-[#00BFFF]' :
                                                    'bg-white/10 text-[#A0AAB2]'
                                                }`} data-testid={`my-bet-status-${i}`}>
                                                    {bet.status === 'pending' && awaitingConfirmation(bet) ? 'AWAITING RESULT' : bet.status?.toUpperCase()}
                                                </span>
                                                <span className="text-xs text-[#A0AAB2]" data-testid="my-bet-placed-at">
                                                    {formatDateCT(bet.placed_at)}
                                                    <span className="ml-1 px-1 py-0.5 rounded bg-white/10 text-[10px] font-bold text-[#FFD700] align-middle">CT</span>
                                                </span>
                                            </div>
                                            {bet.type === 'single' ? (
                                                <>
                                                    <div className="flex items-center gap-2">
                                                        <TeamLogo name={bet.outcome_name} sportKey={bet.sport_key} size={28} />
                                                        <p className="text-sm text-white font-bold">{pickLabel(bet)}</p>
                                                    </div>
                                                    <p className="text-xs text-[#A0AAB2]">{bet.home_team && bet.away_team ? `${bet.away_team} @ ${bet.home_team}` : ''}</p>
                                                    <p className="text-xs text-[#A0AAB2]" data-testid={`my-bet-line-${i}`}>{formatLine(bet)} · <span className="font-mono">{formatOdds(bet.odds)}</span></p>
                                                    {betScores[bet.event_id] && bet.status === 'pending' && (
                                                        <div className="mt-2 p-2 rounded-lg bg-[#FF3B30]/5 border border-[#FF3B30]/20" data-testid={`live-score-${bet.event_id}`}>
                                                            <div className="flex items-center gap-1 mb-1">
                                                                <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3B30] opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF3B30]" /></span>
                                                                <span className="text-[10px] font-bold text-[#FF3B30] uppercase">Live Score</span>
                                                            </div>
                                                            <div className="flex items-center justify-between text-xs">
                                                                <span className="text-white">{betScores[bet.event_id].away_team}</span>
                                                                <span className="font-mono font-black text-white text-sm">{betScores[bet.event_id].away_score}</span>
                                                            </div>
                                                            <div className="flex items-center justify-between text-xs">
                                                                <span className="text-white">{betScores[bet.event_id].home_team}</span>
                                                                <span className="font-mono font-black text-white text-sm">{betScores[bet.event_id].home_score}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <p className="text-sm text-white font-bold">Parlay ({bet.legs?.length} legs)</p>
                                                    {bet.legs?.map((l, li) => (
                                                        <p key={li} className="text-xs text-[#A0AAB2]" data-testid={`my-bet-${i}-leg-${li}`}>
                                                            {pickLabel(l)} <span className="text-[#7a8288]">· {formatLine(l)} · <span className="font-mono">{formatOdds(l.odds)}</span></span>
                                                            <span className="block text-[10px] text-[#666]">{l.away_team} @ {l.home_team}</span>
                                                        </p>
                                                    ))}
                                                </>
                                            )}
                                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
                                                <span className="text-xs text-[#A0AAB2]">Wager: {bet.amount}</span>
                                                <span className={`text-xs font-mono font-bold ${
                                                    bet.status === 'won' ? 'text-[#39FF14]' :
                                                    bet.status === 'lost' ? 'text-[#FF3B30]' :
                                                    bet.status === 'cashed_out' ? 'text-[#00BFFF]' :
                                                    awaitingConfirmation(bet) ? 'text-[#FFA500]' :
                                                    'text-[#39FF14]'
                                                }`}>
                                                    {bet.status === 'won' ? `Won: +${bet.potential_payout}` :
                                                     bet.status === 'lost' ? `Lost: -${bet.amount}` :
                                                     bet.status === 'cashed_out' ? `Cashed Out: +${bet.cashout_amount}` :
                                                     awaitingConfirmation(bet) ? `Pending confirmation` :
                                                     `To Win: ${bet.potential_payout}`}
                                                </span>
                                            </div>
                                            {bet.status === 'pending' && awaitingConfirmation(bet) && (
                                                <div className="mt-2 flex items-start gap-1.5 text-[10px] text-[#FFA500] bg-[#FFA500]/5 border border-[#FFA500]/20 rounded-lg p-2 leading-snug" data-testid={`my-bet-confirm-note-${i}`}>
                                                    <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-px" />
                                                    <span>Game's final — your result is being reviewed. Any win is confirmed before it's paid out.</span>
                                                </div>
                                            )}
                                            {bet.status === 'pending' && !awaitingConfirmation(bet) && (() => {
                                                const q = cashoutQuotes[bet.bet_id];
                                                if (q && !q.available) {
                                                    return (
                                                        <div className="w-full mt-2 text-[10px] text-center text-[#A0AAB2] border border-white/10 rounded py-1.5 px-2" data-testid={`cashout-unavailable-${bet.bet_id}`}>
                                                            {q.reason || 'Cash out unavailable'}
                                                        </div>
                                                    );
                                                }
                                                const amt = q ? q.amount : bet.amount * 0.75;
                                                return (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => cashOutBet(bet.bet_id)}
                                                        className="w-full mt-2 bg-[#FFD700] text-black hover:bg-[#FFD700]/80 text-xs h-8"
                                                        data-testid={`cashout-btn-${bet.bet_id}`}
                                                    >
                                                        <DollarSign className="w-3 h-3 mr-1" />
                                                        Cash Out ({Number(amt).toFixed(2)})
                                                        {q?.live && <span className="ml-1.5 text-[9px] font-black text-[#FF3B30] bg-[#FF3B30]/15 px-1 rounded">LIVE</span>}
                                                    </Button>
                                                );
                                            })()}
                                            <button
                                                onClick={() => setReceiptBet(bet)}
                                                className="w-full mt-2 flex items-center justify-center gap-1.5 text-[11px] text-[#A0AAB2] hover:text-[#FFD700] border border-white/10 hover:border-[#FFD700]/40 rounded py-1.5 transition-colors"
                                                data-testid={`bet-receipt-btn-${i}`}
                                            >
                                                <Receipt className="w-3.5 h-3.5" /> Receipt
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Shareable / printable bet receipt */}
            <AnimatePresence>
                {receiptBet && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
                        onClick={() => setReceiptBet(null)}
                        data-testid="bet-receipt-modal"
                    >
                        <motion.div
                            initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 12 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-sm rounded-2xl bg-[#0e0e0e] border border-[#FFD700]/30 overflow-hidden"
                        >
                            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-gradient-to-r from-[#1a1400] to-[#0e0e0e]">
                                <div className="flex items-center gap-2">
                                    <Receipt className="w-4 h-4 text-[#FFD700]" />
                                    <span className="text-sm font-black tracking-widest text-[#FFD700]">BETOHALICS</span>
                                </div>
                                <button onClick={() => setReceiptBet(null)} data-testid="bet-receipt-close" className="text-[#A0AAB2] hover:text-white"><X className="w-4 h-4" /></button>
                            </div>
                            <div className="p-4 space-y-3">
                                <div className="text-center">
                                    <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2]">Bet Receipt</p>
                                    <p className="text-xs font-mono text-white">Ticket #{receiptTicketNo(receiptBet)}</p>
                                    <p className="text-[11px] text-[#A0AAB2]">
                                        {formatDateCT(receiptBet.placed_at)}
                                        <span className="ml-1 px-1 py-0.5 rounded bg-white/10 text-[9px] font-bold text-[#FFD700] align-middle">CT</span>
                                    </p>
                                </div>
                                <p className="text-[11px] font-bold uppercase tracking-wider text-white">
                                    {receiptBet.type === 'single' ? 'Single Bet' : `Parlay · ${receiptBet.legs?.length || 0} legs`}
                                </p>
                                <div className="space-y-2">
                                    {receiptRows(receiptBet).map((r, ri) => (
                                        <div key={ri} className="border-b border-dashed border-white/10 pb-2" data-testid={`bet-receipt-leg-${ri}`}>
                                            <p className="text-sm font-bold text-white">{r.pick}</p>
                                            <p className="text-xs text-[#A0AAB2]">{r.line} · <span className="font-mono text-white">{r.odds}</span></p>
                                            <p className="text-[10px] text-[#666]">{r.match}</p>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex items-center justify-between text-xs pt-1">
                                    <span className="text-[#A0AAB2]">Wager</span>
                                    <span className="font-mono text-white">{receiptBet.amount}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-[#A0AAB2]">Result</span>
                                    <span className={`font-mono font-black ${
                                        receiptBet.status === 'won' ? 'text-[#39FF14]' :
                                        receiptBet.status === 'lost' ? 'text-[#FF3B30]' :
                                        receiptBet.status === 'cashed_out' ? 'text-[#00BFFF]' : 'text-[#39FF14]'
                                    }`}>{receiptResult(receiptBet)}</span>
                                </div>
                                <p className="text-[9px] text-center text-[#666] pt-1">Line &amp; odds locked at time of purchase</p>
                                <div className="flex gap-2 pt-1">
                                    <Button onClick={() => shareReceipt(receiptBet)} className="flex-1 bg-[#FFD700] text-black hover:bg-[#FFD700]/80 text-xs h-9" data-testid="bet-receipt-share">
                                        <Share2 className="w-3.5 h-3.5 mr-1" /> Share
                                    </Button>
                                    <Button onClick={() => printReceipt(receiptBet)} variant="outline" className="flex-1 border-white/20 text-white hover:bg-white/10 text-xs h-9" data-testid="bet-receipt-print">
                                        <Printer className="w-3.5 h-3.5 mr-1" /> Print
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};


const LiveExtraMarkets = ({ event, state, onToggle, addToBetSlip, isInSlip }) => {
    const open = !!state?.open;
    const groups = [...(state?.periods || []), ...(state?.props || [])];
    return (
        <div className="mt-3 pt-3 border-t border-white/5">
            <button
                onClick={onToggle}
                className="text-xs font-bold text-[#A855F7] hover:text-[#A855F7]/80 flex items-center gap-1"
                data-testid={`live-extra-toggle-${event.id}`}
            >
                {open ? <ChevronDown className="w-3 h-3 rotate-180" /> : <ChevronRight className="w-3 h-3" />}
                More live markets · Props &amp; Halves
            </button>
            {open && (
                <div className="mt-3 space-y-3" data-testid={`live-extra-${event.id}`}>
                    {state?.loading ? (
                        <div className="flex items-center gap-2 text-xs text-[#A0AAB2]"><Loader2 className="w-4 h-4 animate-spin" /> Loading live markets…</div>
                    ) : groups.length === 0 ? (
                        <p className="text-xs text-[#A0AAB2]">No extra live markets open for this game right now.</p>
                    ) : groups.map(m => (
                        <div key={m.key}>
                            <div className="flex items-center justify-between mb-1.5">
                                <p className="text-xs font-bold text-[#A855F7]">{m.label}</p>
                                <span className="text-[10px] text-[#666]">{m.bookmaker}</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {m.outcomes.map((o, oi) => (
                                    <button
                                        key={oi}
                                        onClick={() => addToBetSlip(event, m.key, o)}
                                        className={`px-2.5 py-1.5 rounded-lg text-center min-w-[64px] border text-xs ${
                                            isInSlip(event.id, m.key, o.description && !o.name.startsWith(`${o.description} `) ? `${o.description} ${o.name}${o.point != null ? ' ' + o.point : ''}` : o.name)
                                                ? 'bg-[#39FF14]/20 border-[#39FF14] text-[#39FF14]'
                                                : 'bg-white/5 border-white/10 text-white hover:bg-white/10'
                                        }`}
                                        data-testid={`live-extra-odds-${event.id}-${m.key}-${o.name}`}
                                    >
                                        <span className="block text-[10px] text-[#A0AAB2] truncate max-w-[120px]">
                                            {o.description ? `${o.description} ` : ''}{o.name}{o.point != null ? ` ${o.point > 0 ? '+' : ''}${o.point}` : ''}
                                        </span>
                                        <span className={`font-mono font-bold ${o.price > 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                            {formatOdds(o.price)}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const BetSlipPanel = ({ user, betSlip, betAmounts, setBetAmounts, removeBet, clearBetSlip, placeBets, placingBet, betMode, setBetMode, parlayAmount, setParlayAmount, mobile, limits, liveSlipStatus = {}, acceptNewOdds }) => {
    const isParlay = betMode === 'parlay' && betSlip.length >= 2;

    const totalStakeSingles = betSlip.reduce((sum, b) => sum + (parseFloat(betAmounts[b.key]) || 10), 0);
    const totalPayoutSingles = betSlip.reduce((sum, b) => {
        const amt = parseFloat(betAmounts[b.key]) || 10;
        return sum + parseFloat(calcPayout(amt, b.odds));
    }, 0);

    // Parlay calculations
    const parlayAmt = parseFloat(parlayAmount) || 10;
    const combinedDecimalOdds = betSlip.reduce((prod, b) => {
        const d = b.odds > 0 ? (b.odds / 100) + 1 : (100 / Math.abs(b.odds)) + 1;
        return prod * d;
    }, 1);
    const parlayPayout = (parlayAmt * combinedDecimalOdds).toFixed(2);

    // ── Same-Game Parlay (SGP) live quote ──
    // Any repeated event in the slip means legs are correlated → the server prices
    // them together (correlation-adjusted, house-safe). We fetch a debounced quote
    // so the slip shows the REAL odds/payout (and any conflict block) before placing.
    const [parlayQuote, setParlayQuote] = useState(null);
    useEffect(() => {
        if (!isParlay) { setParlayQuote(null); return; }
        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                const { data } = await axios.post(`${API}/sports/parlay/quote`, {
                    legs: betSlip.map(b => ({
                        event_id: b.event_id, sport_key: b.sport_key, market: b.market,
                        outcome_name: b.outcome_name, odds: b.odds, point: b.point, amount: parlayAmt,
                        home_team: b.home_team, away_team: b.away_team, commence_time: b.commence_time,
                    })),
                    total_amount: parlayAmt,
                }, { withCredentials: true });
                if (!cancelled) setParlayQuote(data);
            } catch { if (!cancelled) setParlayQuote(null); }
        }, 300);
        return () => { cancelled = true; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isParlay, JSON.stringify(betSlip.map(b => [b.event_id, b.market, b.outcome_name, b.point, b.odds])), parlayAmt]);

    const isSgp = !!(parlayQuote && parlayQuote.ok && parlayQuote.is_sgp);
    const isBoosted = !!(parlayQuote && parlayQuote.ok && parlayQuote.boosted);
    const sgpBlocked = !!(parlayQuote && parlayQuote.blocked);
    // Prefer boosted price > server SGP quote > naive client calc.
    const displayDecimal = isBoosted ? parlayQuote.boosted_decimal
        : (parlayQuote && parlayQuote.ok) ? parlayQuote.combined_decimal : combinedDecimalOdds;
    const displayPayout = isBoosted ? Number(parlayQuote.boosted_payout).toFixed(2)
        : (parlayQuote && parlayQuote.ok) ? Number(parlayQuote.payout).toFixed(2) : parlayPayout;
    const preBoostDecimal = (parlayQuote && parlayQuote.ok) ? parlayQuote.combined_decimal : combinedDecimalOdds;


    // ── Client-side risk pre-check (mirrors backend routes/sports_risk.py) ──
    // Blocks the wager and shows a clean message BEFORE submission so the
    // customer never sees a post-submit rejection.
    const limitViolations = [];       // hard-blocks placing
    const parlayPayoutCapped = { on: false, cap: 0 }; // informational (parlay payout is capped, not blocked)
    if (limits) {
        const g = limits.global || {};
        const ticketCap = Number(g.max_ticket_payout ?? Infinity);
        if (isParlay) {
            if (isSgp) {
                // Same-Game Parlay uses the dedicated SGP limits (2+ legs allowed,
                // per-game cap, correlation-adjusted payout).
                const s = limits.sgp || limits.parlay || {};
                const maxLegs = Number(s.max_legs ?? 6);
                const maxWager = Number(s.max_wager ?? Infinity);
                if (betSlip.length < 2) limitViolations.push('A same-game parlay needs at least 2 legs.');
                if (betSlip.length > maxLegs) limitViolations.push(`Up to ${maxLegs} selections from one game.`);
                if (parlayAmt > maxWager + 1e-9) limitViolations.push(`Max $${maxWager.toFixed(2)} wager per same-game parlay.`);
                const cap = Math.min(Number(s.max_payout ?? Infinity), ticketCap);
                if (parseFloat(displayPayout) > cap + 1e-9) { parlayPayoutCapped.on = true; parlayPayoutCapped.cap = cap; }
            } else {
                const p = limits.parlay || {};
                const minLegs = Number(p.min_legs ?? 2);
                const maxLegs = Number(p.max_legs ?? 12);
                const maxWager = Number(p.max_wager ?? Infinity);
                if (betSlip.length < minLegs) limitViolations.push(`Parlays need at least ${minLegs} legs.`);
                if (betSlip.length > maxLegs) limitViolations.push(`Parlays allow at most ${maxLegs} legs.`);
                if (parlayAmt > maxWager + 1e-9) limitViolations.push(`Max $${maxWager.toFixed(2)} wager per parlay.`);
                const cap = Math.min(Number(p.max_payout ?? Infinity), ticketCap);
                if (parseFloat(parlayPayout) > cap + 1e-9) { parlayPayoutCapped.on = true; parlayPayoutCapped.cap = cap; }
            }
        } else {
            betSlip.forEach((b) => {
                const isProp = String(b.market || '').toLowerCase().startsWith('player_');
                const t = (isProp ? limits.prop : limits.straight) || {};
                const label = isProp ? 'player prop' : 'straight bet';
                const amt = parseFloat(betAmounts[b.key]) || 10;
                const maxWager = Number(t.max_wager ?? Infinity);
                const cap = Math.min(Number(t.max_payout ?? Infinity), ticketCap);
                const payout = parseFloat(calcPayout(amt, b.odds));
                if (amt > maxWager + 1e-9) limitViolations.push(`${b.outcome_name}: max $${maxWager.toFixed(2)} per ${label}.`);
                else if (payout > cap + 1e-9) limitViolations.push(`${b.outcome_name}: payout exceeds the $${cap.toFixed(2)} ${label} limit — lower your wager.`);
            });
        }
    }
    const blocked = limitViolations.length > 0;
    const liveSuspended = betSlip.some(b => liveSlipStatus[b.key]);
    const placeDisabled = placingBet || blocked || liveSuspended || sgpBlocked;
    const tid = (base) => (mobile ? `${base}-mobile` : base);

    return (
        <div className={mobile ? '' : 'sticky top-4'}>
            {!mobile && (
                <div className="card-dark p-4 mb-0 rounded-b-none border-b border-white/10">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-bold text-white flex items-center gap-2">
                            <Ticket className="w-5 h-5 text-[#39FF14]" /> Bet Slip ({betSlip.length})
                        </h3>
                        {betSlip.length > 0 && (
                            <button onClick={clearBetSlip} className="text-xs text-[#A0AAB2] hover:text-[#FF3B30] flex items-center gap-1" data-testid="clear-betslip-btn">
                                <Trash2 className="w-3 h-3" /> Clear
                            </button>
                        )}
                    </div>
                </div>
            )}
            {mobile && betSlip.length > 0 && (
                <div className="flex justify-end mb-2">
                    <button onClick={clearBetSlip} className="text-xs text-[#A0AAB2] hover:text-[#FF3B30] flex items-center gap-1" data-testid="clear-betslip-btn-mobile">
                        <Trash2 className="w-3 h-3" /> Clear All
                    </button>
                </div>
            )}

            {/* Free-play wager progress (compact). Auto-hides when no rollover. */}
            <div className="mb-2">
                <FreePlayProgressBadge user={user} compact />
            </div>

            {/* Empty state — shown on both mobile drawer and desktop sidebar */}
            {betSlip.length === 0 && (
                <div className={`${mobile ? '' : 'card-dark rounded-t-none p-6'} text-center py-10 px-4`}
                     data-testid="bet-slip-empty">
                    <Ticket className="w-10 h-10 mx-auto mb-2 text-[#39FF14]/30" />
                    <p className="text-sm font-bold text-white mb-1">Your bet slip is empty</p>
                    <p className="text-[11px] text-[#A0AAB2] mb-3 leading-snug">
                        Tap any green odds button on a game to add a selection.
                    </p>
                    <div className="inline-block px-3 py-2 rounded-lg text-[10px] text-[#FFD700] border border-[#FFD700]/30"
                         style={{ background: 'rgba(255,215,0,0.08)' }}>
                        💡 Add <b>2 or more</b> selections to combine them into a <b>PARLAY</b> for bigger payouts
                    </div>
                </div>
            )}

            {/* Singles / Parlay Toggle */}
            {betSlip.length >= 2 && (
                <div className="flex rounded-lg overflow-hidden border border-white/10 mb-3">
                    <button
                        onClick={() => setBetMode('singles')}
                        className={`flex-1 py-2 text-xs font-bold transition-colors ${betMode === 'singles' ? 'bg-[#39FF14] text-black' : 'bg-[#121212] text-[#A0AAB2] hover:text-white'}`}
                        data-testid="mode-singles-btn"
                    >
                        Singles ({betSlip.length})
                    </button>
                    <button
                        onClick={() => setBetMode('parlay')}
                        className={`flex-1 py-2 text-xs font-bold transition-colors ${betMode === 'parlay' ? 'bg-[#FFD700] text-black' : 'bg-[#121212] text-[#A0AAB2] hover:text-white'}`}
                        data-testid="mode-parlay-btn"
                    >
                        Parlay ({betSlip.length} legs)
                    </button>
                </div>
            )}

            {betSlip.length > 0 && (
            <>
            <div className={`${mobile ? '' : 'card-dark rounded-t-none'} space-y-2 ${mobile ? '' : 'p-4 max-h-[50vh] overflow-y-auto'}`}>
                {betSlip.map(bet => (
                    <div key={bet.key} className="p-3 rounded-xl border border-white/10 bg-[#0a0a0a]" data-testid={`slip-${bet.key}`}>
                        <div className="flex items-start justify-between mb-1">
                            <div className="min-w-0 flex-1">
                                <p className="text-xs text-[#A0AAB2] truncate">{bet.matchup}</p>
                                <div className="flex items-center gap-2">
                                    <TeamLogo name={bet.outcome_name} sportKey={bet.sportKey || bet.sport_key} size={26} />
                                    <p className="text-sm text-white font-bold truncate">{bet.outcome_name}</p>
                                </div>
                                <p className="text-xs text-[#A0AAB2]">{bet.market}{bet.point ? ` (${bet.point > 0 ? '+' : ''}${bet.point})` : ''}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <span className={`text-sm font-mono font-bold ${bet.odds > 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                    {formatOdds(bet.odds)}
                                </span>
                                <button onClick={() => removeBet(bet.key)} className="text-[#A0AAB2] hover:text-[#FF3B30]">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        {liveSlipStatus[bet.key]?.suspended === 'changed' && (
                            <div className="mt-1.5 flex items-center justify-between gap-2 rounded-lg border border-[#FFD700]/40 bg-[#FFD700]/10 px-2 py-1.5" data-testid={tid(`slip-odds-changed-${bet.key}`)}>
                                <span className="text-[10px] font-bold text-[#FFD700] leading-snug">
                                    Live odds moved to {formatOdds(liveSlipStatus[bet.key].newOdds)}
                                </span>
                                <button onClick={() => acceptNewOdds?.(bet.key, liveSlipStatus[bet.key].newOdds)}
                                    className="text-[10px] font-black px-2 py-1 rounded bg-[#FFD700] text-black hover:bg-[#FFD700]/80 shrink-0"
                                    data-testid={tid(`slip-accept-odds-${bet.key}`)}>
                                    Accept
                                </button>
                            </div>
                        )}
                        {liveSlipStatus[bet.key]?.suspended === 'unavailable' && (
                            <div className="mt-1.5 flex items-center justify-between gap-2 rounded-lg border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-2 py-1.5" data-testid={tid(`slip-suspended-${bet.key}`)}>
                                <span className="text-[10px] font-bold text-[#FF3B30] leading-snug">
                                    Market suspended (live) — remove to continue
                                </span>
                                <button onClick={() => removeBet(bet.key)}
                                    className="text-[10px] font-black px-2 py-1 rounded bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80 shrink-0">
                                    Remove
                                </button>
                            </div>
                        )}
                        {!isParlay && (
                            <>
                                <Input
                                    type="number"
                                    placeholder="10"
                                    value={betAmounts[bet.key] || ''}
                                    onChange={e => setBetAmounts(prev => ({...prev, [bet.key]: e.target.value}))}
                                    className="mt-2 h-8 bg-[#121212] border-white/10 text-white text-sm"
                                    data-testid={tid(`bet-amount-${bet.key}`)}
                                />
                                <SmartWagerHint
                                    user={user}
                                    currentAmount={betAmounts[bet.key]}
                                    onFill={(v) => setBetAmounts(prev => ({...prev, [bet.key]: v}))}
                                />
                                <p className="text-[10px] text-[#A0AAB2] mt-1">
                                    To Win: <span className="text-[#39FF14] font-mono">{calcPayout(parseFloat(betAmounts[bet.key]) || 10, bet.odds)}</span>
                                </p>
                            </>
                        )}
                    </div>
                ))}
            </div>

            <div className={`${mobile ? 'mt-3' : 'card-dark rounded-t-none border-t border-white/10 p-4'}`}>
                {isParlay ? (
                    <>
                        {isSgp && (
                            <div className="mb-3 flex items-center gap-2 rounded-lg border border-[#FFD700]/30 bg-[#FFD700]/10 px-3 py-2" data-testid={tid('sgp-badge')}>
                                <Ticket className="w-4 h-4 text-[#FFD700] shrink-0" />
                                <span className="text-[11px] text-[#FFD700] font-bold uppercase tracking-wide">Same Game Parlay</span>
                                <span className="text-[10px] text-[#A0AAB2]">· correlated legs priced together</span>
                            </div>
                        )}
                        {isBoosted && (
                            <div className="mb-3 flex items-center gap-2 rounded-lg border border-[#39FF14]/40 bg-[#39FF14]/10 px-3 py-2 animate-pulse" data-testid={tid('boost-badge')}>
                                <Zap className="w-4 h-4 text-[#39FF14] shrink-0" />
                                <span className="text-[11px] text-[#39FF14] font-bold uppercase tracking-wide">
                                    Boosted{parlayQuote.boost_pct ? ` +${parlayQuote.boost_pct}%` : ''}
                                </span>
                                <span className="text-[10px] text-[#A0AAB2] truncate">· {parlayQuote.boost_name || 'Odds boost applied'}</span>
                            </div>
                        )}
                        {sgpBlocked && (
                            <div className="mb-3 rounded-lg border border-[#FF3B30]/30 bg-[#FF3B30]/10 px-3 py-2 text-[11px] text-[#FF6B6B]" data-testid={tid('sgp-conflict-note')}>
                                {parlayQuote.message || 'These selections can\u2019t be combined.'}
                            </div>
                        )}
                        <div className="mb-3">
                            <label className="text-xs text-[#A0AAB2] mb-1 block">Parlay Wager</label>
                            <Input
                                type="number"
                                placeholder="10"
                                value={parlayAmount}
                                onChange={e => setParlayAmount(e.target.value)}
                                className="h-8 bg-[#121212] border-white/10 text-white text-sm"
                                data-testid={tid('parlay-amount-input')}
                            />
                            <SmartWagerHint
                                user={user}
                                currentAmount={parlayAmount}
                                onFill={(v) => setParlayAmount(v)}
                            />
                        </div>
                        <div className="flex items-center justify-between mb-1 text-xs">
                            <span className="text-[#A0AAB2]">{isBoosted ? 'Boosted Odds' : isSgp ? 'SGP Odds' : 'Combined Odds'}</span>
                            <span className={`font-mono font-bold ${isBoosted ? 'text-[#39FF14]' : 'text-[#FFD700]'}`} data-testid={tid('parlay-combined-odds')}>
                                {displayDecimal >= 2 ? `+${((displayDecimal - 1) * 100).toFixed(0)}` : `-${(100 / (displayDecimal - 1)).toFixed(0)}`}
                            </span>
                        </div>
                        {(isSgp || isBoosted) && (
                            <div className="flex items-center justify-between mb-1 text-[10px] text-[#A0AAB2]">
                                <span>{isBoosted ? 'Was' : 'Straight-multiply odds'}</span>
                                <span className="font-mono line-through opacity-70">
                                    {preBoostDecimal >= 2 ? `+${((preBoostDecimal - 1) * 100).toFixed(0)}` : `-${(100 / (preBoostDecimal - 1)).toFixed(0)}`}
                                </span>
                            </div>
                        )}
                        <div className="flex items-center justify-between mb-1 text-xs">
                            <span className="text-[#A0AAB2]">Stake</span>
                            <span className="font-mono font-bold text-white">{parlayAmt.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between mb-3 text-xs">
                            <span className="text-[#A0AAB2]">Potential Payout</span>
                            <span className="font-mono font-bold text-[#39FF14]" data-testid={tid('parlay-payout')}>{displayPayout}</span>
                        </div>
                        <p className="text-[10px] text-[#FF3B30]/80 mb-3">All legs must win for payout. One loss = entire parlay lost.</p>
                        {parlayPayoutCapped.on && (
                            <p className="text-[10px] text-[#FFD700] mb-3" data-testid={tid('parlay-payout-capped-note')}>
                                ⚠️ Payout will be capped at ${parlayPayoutCapped.cap.toFixed(2)} (Betohalics parlay limit).
                            </p>
                        )}
                    </>
                ) : (
                    <>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-[#A0AAB2]">Total Stake</span>
                            <span className="text-sm font-mono font-bold text-white">{totalStakeSingles.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-sm text-[#A0AAB2]">Total Payout</span>
                            <span className="text-sm font-mono font-bold text-[#39FF14]">{totalPayoutSingles.toFixed(2)}</span>
                        </div>
                    </>
                )}
                {blocked && (
                    <div className="mb-3 rounded-lg border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-2.5" data-testid={tid('bet-limit-warning')}>
                        <p className="text-[11px] font-bold text-[#FF3B30] mb-1">Wager exceeds Betohalics limits</p>
                        <ul className="space-y-0.5">
                            {limitViolations.map((m, i) => (
                                <li key={i} className="text-[10px] text-[#FF3B30]/90 leading-snug">• {m}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {liveSuspended && !blocked && (
                    <div className="mb-3 rounded-lg border border-[#FFD700]/40 bg-[#FFD700]/10 p-2.5" data-testid={tid('bet-live-suspended-warning')}>
                        <p className="text-[11px] font-bold text-[#FFD700] leading-snug">Live odds changed — review the highlighted picks and Accept the new price (or remove) to place.</p>
                    </div>
                )}
                <Button
                    className="w-full bg-[#39FF14] text-black hover:bg-[#39FF14]/80 font-bold"
                    onClick={placeBets}
                    disabled={placeDisabled}
                    data-testid={tid('place-bets-btn')}
                >
                    {placingBet ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <TrendingUp className="w-4 h-4 mr-2" />}
                    {placingBet ? 'Placing...' : blocked ? 'Reduce wager to continue' : liveSuspended ? 'Review live odds to continue' : isParlay ? `Place Parlay (${betSlip.length} legs)` : `Place Bet${betSlip.length > 1 ? 's' : ''}`}
                </Button>
                <div className="mt-2.5 flex items-start gap-1.5 text-[10px] text-[#A0AAB2] leading-snug" data-testid={tid('payout-confirm-note')}>
                    <ShieldCheck className="w-3.5 h-3.5 text-[#39FF14] shrink-0 mt-px" />
                    <span>Winning tickets are reviewed and confirmed before any payout is credited.</span>
                </div>
            </div>
            </>
            )}
        </div>
    );
};

export default Sportsbook;
